import { createVoiceTiming } from '@/lib/voice-timing';
import type { Prayer, ScriptureAnchor } from '@/types/prayer';
import { useAdiaStore } from '@/lib/store';
import { transcribeAudio, ParseError, extFromMimeType } from '@/lib/grok';
import * as db from '@/lib/db';
import {
  audioQueueMetaCarriesBytes,
  captureQueueListFence,
  setQueueItemReader,
  useAudioQueueStore,
  type QueueFence,
  type QueueItemMeta,
  type RecordingAttention,
} from '@/lib/audio-queue-store';
import { getSupabase } from '@/lib/supabase';
import {
  CoreReadOnlyError,
  assertCoreWriteAllowed,
  enhancedVoiceAllowed,
  isCoreReadOnly,
} from '@/lib/core-cutover';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { createSingleFlightDrain } from '@/lib/single-flight-drain';
import {
  containsRecoveredVoiceText,
  normalizeRecoveryKind,
} from '@/lib/audio-recovery';
import type {
  AudioRecoveryKind,
  RecoveredAudioCommitClaim,
} from '@/types/audio-recovery';
import type { AudioCaptureMode } from '@/types/audio-recovery';
import {
  getCurrentContentLifecycleContext,
  hasAuthoritativeContentReceipt,
  syncContentLifecycle,
} from '@/lib/content-lifecycle/sync';
import {
  attachInlineAudioToPendingOperation,
  authorRecoveredPrayerTranscription,
  getLocalContentEntity,
  type ContentLifecycleAuthExpectation,
  type ContentLifecycleContext,
} from '@/lib/content-lifecycle/store';
import { parseScriptureAnchor, scriptureAnchorsEqual } from '@/lib/scripture-anchor';
import {
  getAuthEpoch,
  getAuthUserId,
  isAuthResolved,
  waitForAuthResolution,
} from '@/lib/keepsake/epochs';
import {
  AudioRegistryProtocolError,
  bindRegistryToContentReceipt,
  claimRegistryRecovery,
  discardRegistryAudio,
  discoverRegistryAudio,
  markRegistryParked,
  markRegistryUploaded,
  reconcileRegistryAudio,
  registerDurableAudio,
  retryRegistryAudio,
} from '@/lib/audio-registry';

// Core-content cutover and Enhanced Voice posture for the audio queue.
//
// The queued audio bytes may be the ONLY copy of a prayer the user has
// spoken but never seen transcribed, so this module is the most
// preservation-sensitive surface in the client. Two shapes:
//
//   - enqueueAudio THROWS (no new capture is accepted at all; the voice
//     layer is disabled upstream, this is the backstop).
//   - every processor / retry / dequeue driver EARLY-RETURNS as a
//     no-op. They are the paths that would delete a row, delete a
//     Storage object, or mutate a linked prayer. A no-op leaves every
//     byte exactly where the operator export will find it.
//   - the independent voice gate also blocks upload, transcription, and
//     retry work when typed core writes are enabled but cloud audio is not.

/**
 * Private Supabase Storage bucket holding enhanced-capture bytes. Migration
 * 044 retires the legacy broad browser policies: uploads now use only the
 * exact canonical path and signed capability returned by the owner-scoped
 * registry route; browser deletes are never authoritative.
 */
const STORAGE_BUCKET = 'prayer-audio';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const AUDIO_EXTENSION_ALLOWLIST = new Set(['webm', 'm4a', 'mp3', 'wav', 'ogg']);
const UPLOAD_PROVED_REGISTRY_STATES = new Set([
  'uploaded',
  'transcribing',
  'transcribed',
  'retry',
  'failed',
  'parked',
]);
const REGISTRY_PROCESSING_STATES = new Set([
  'registered',
  ...UPLOAD_PROVED_REGISTRY_STATES,
  'quarantined',
]);

function compareRegistryRevision(left: string, right: string): number {
  if (!/^[1-9][0-9]*$/.test(left) || !/^[1-9][0-9]*$/.test(right)) {
    throw new Error('registered-audio-revision-invalid');
  }
  const a = BigInt(left);
  const b = BigInt(right);
  return a === b ? 0 : a > b ? 1 : -1;
}

/**
 * Source surface that produced an inline-save orphan. Three-way (never
 * conflates absent with unknown):
 *   - absent/null (legacy rows + all pre-bookmarks flows) -> 'prayer'
 *   - exact 'prayer' -> 'prayer'
 *   - exact 'bookmark' -> 'bookmark'
 *   - anything else (malformed / future) -> 'unknown'
 * Only 'prayer' is claimable into a Prayer (positive allowlist in
 * useVoiceCapture). 'unknown' matches neither source-scoped clear, a
 * defensive dead-end that should never arise since the app only ever
 * writes 'prayer' or 'bookmark'.
 */
export function normalizeSource(
  v: unknown,
): 'prayer' | 'bookmark' | 'unknown' {
  if (v == null) return 'prayer';
  if (v === 'prayer') return 'prayer';
  if (v === 'bookmark') return 'bookmark';
  return 'unknown';
}

function queueMeta(row: db.AudioQueueRow): QueueItemMeta {
  return {
    id: row.id,
    ownerId: row.ownerId,
    authEpochAtCapture: row.authEpochAtCapture,
    audioProtocolVersion: row.audioProtocolVersion,
    captureMode: row.captureMode,
    destinationId: row.destinationId,
    sourceDeviceId: row.sourceDeviceId,
    prayerId: row.prayerId,
    mimeType: row.mimeType,
    capturedAt: row.capturedAt,
    language: row.language,
    transcribedText: row.transcribedText,
    attemptCount: row.attemptCount,
    failed: row.failed,
    sizeBytes: row.sizeBytes ?? row.blob?.size ?? 0,
    storagePath: row.storagePath,
    awaitingInlineSave: row.awaitingInlineSave,
    inlineSaveSource: row.inlineSaveSource,
    scriptureAnchor: row.scriptureAnchor,
    recoveryKind: row.recoveryKind,
    byteSha256: row.byteSha256,
    localDisposition: row.localDisposition,
    registryRevision: row.registryRevision,
    registryState: row.registryState,
    destinationState: row.destinationState,
    destinationEntityType: row.destinationEntityType,
    destinationEntityId: row.destinationEntityId,
    destinationSubId: row.destinationSubId,
    destinationOpId: row.destinationOpId,
    recoveryLeaseToken: row.recoveryLeaseToken,
    recoveryLeaseExpiresAt: row.recoveryLeaseExpiresAt,
  };
}

// The list's fenced same-session refresh re-reads one durable row through
// this module's mapping (audio-queue-store.ts refreshItem).
setQueueItemReader(async (id) => {
  const row = await db.getAudioQueueRow(id);
  return row ? queueMeta(row) : null;
});

/** In-memory list updates; each takes the fence its operation captured at entry. */
const queueList = () => useAudioQueueStore.getState();

/**
 * Open the in-memory list for one owner runtime (plan rev 4.1, section 7):
 * a fresh runtime id for every OwnerScopedRuntime effect run, bound to the
 * current owner and auth epoch. Returns the id for `closeQueueListScope`, or
 * null (no scope, so no list) when auth is not a resolved account.
 */
export function openQueueListScope(): string | null {
  const ownerId = getAuthUserId();
  if (typeof ownerId !== 'string') return null;
  const runtimeId = crypto.randomUUID();
  queueList().openScope({ runtimeId, ownerId, authEpoch: getAuthEpoch() });
  return runtimeId;
}

/** Close the list only if that runtime still owns it. */
export function closeQueueListScope(runtimeId: string | null): void {
  if (runtimeId) queueList().closeScope(runtimeId);
}

/**
 * The owner runtime's list scope. The runtime can mount before the auth-epoch
 * module has resolved the signed-in user (the owner gate and that module read
 * the session independently), so the scope opens as soon as auth resolves;
 * `ready` settles once it has opened (true) or cannot (false). `close` runs at
 * the runtime's cleanup: a scope that has not opened yet never will.
 */
export function startQueueListScope(): { ready: Promise<boolean>; close: () => void } {
  let runtimeId: string | null = null;
  let closed = false;
  const open = (): boolean => {
    if (closed) return false;
    runtimeId = openQueueListScope();
    return runtimeId !== null;
  };
  const ready = isAuthResolved()
    ? Promise.resolve(open())
    : waitForAuthResolution().then(open, () => false);
  return {
    ready,
    close: () => {
      closed = true;
      closeQueueListScope(runtimeId);
    },
  };
}

/**
 * Audio queue processor (B.2). Walks captured-but-not-transcribed
 * audio entries in IDB, transcribes each via the existing
 * `transcribeAudio` helper (which carries its own 502 retry semantics),
 * and routes the resulting text to the linked Prayer when one exists
 * — or stores the text on the queue item for the resume card to
 * consume on "Continue recording".
 *
 * Retry budget per locked decision E.5 addition:
 *   3 attempts × 2 cycles spaced 60s apart = 6 total attempts over
 *   ~2 minutes. After exhaustion, the item's `failed` flag flips true
 *   and the linked Prayer's `transcriptionFailed` flag flips true.
 *   The audio blob is RETAINED in IDB until the user runs V.B's recovery
 *   flow. When Enhanced Voice is enabled, committing a typed replacement
 *   releases it; while voice is disabled, the typed Prayer commits but the
 *   exact queue row and audio bytes remain terminally parked.
 *
 * V.B.1 terminal short-circuit: when transcribeAudio throws a ParseError
 * with `terminal: true` (413 payload-too-large, other 4xx malformed),
 * processOne calls `markRowTerminallyFailed` directly instead of
 * `bumpAttempt`. End-state is identical to a 6-attempt exhaust; reached
 * in one attempt because retrying the same blob will fail identically.
 *
 * Note that `transcribeAudio` already retries certain status codes
 * (502 / 503 fast, 504 / 408 slow) up to 3 times internally per V.B.1.
 * Each "attempt" from the queue's perspective is a fresh call to
 * transcribeAudio (which itself does 1-3 internal retries). So the
 * effective network round-trip count for retry-eligible errors is
 * 6-18, not 6 — but the USER-FACING retry budget is 6 attempts because
 * that's what the queue processor counts.
 */

const MAX_QUEUE_ATTEMPTS = 6;
const RETRY_CYCLE_MS = 60_000;

/**
 * V.A.2: read the current authenticated user's id from the in-memory
 * Supabase session cache. Used by `uploadAudioToStorage` to construct
 * the per-user folder prefix and by `processOne`'s migration branch
 * to gate uploads. Returns null when no session exists (anonymous /
 * signed-out user) — the caller decides what to do.
 *
 * `auth.getSession()` is cached after the first hit, so this is fast
 * on the hot path. The path-traversal check on the server (rule #31)
 * also enforces userId match, so a stale userId returned here would
 * 403 server-side rather than leak audio across users.
 */
async function getCurrentUserId(): Promise<string | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data.session?.user?.id ?? null;
}

/**
 * Voice-capture custody's continuation authority (account-change recording
 * plan rev 4.1, section 3.4). A capture custody still tracks may take its next
 * step only while it is not retired and its original owner and auth epoch are
 * current. Upload, registration and transcription ask before every network
 * call, row write and list update, and pass the same question into the
 * registry and transcription helpers, so a held response cannot act for a
 * retired capture, even after A to B to A. The request already issued may
 * finish; its continuation does nothing. Rows custody does not track (the
 * background processor's) are not affected.
 */
let captureContinuationGuard: ((queueId: string) => boolean) | null = null;

export function setCaptureContinuationGuard(guard: (queueId: string) => boolean): void {
  captureContinuationGuard = guard;
}

/** The error a refused continuation throws (not a service failure). */
export const CAPTURE_RETIRED_ERROR = 'audio-capture-retired';

/**
 * Part R: the owner, auth epoch and runtime continuation of an explicit run
 * custody does not track (bookmark word preparation), keyed by queue id while
 * that run is active. Every helper that asks `captureMayContinue` then stops on
 * sign-out, a scope change or A to B to A exactly as a live capture does.
 */
const explicitContinuations = new Map<string, () => boolean>();

function captureMayContinue(queueId: string): boolean {
  try {
    const explicit = explicitContinuations.get(queueId);
    if (explicit && !explicit()) return false;
    return captureContinuationGuard?.(queueId) ?? true;
  } catch {
    return false;
  }
}

function assertCaptureMayContinue(queueId: string): void {
  if (!captureMayContinue(queueId)) throw new Error(CAPTURE_RETIRED_ERROR);
}

/**
 * Recheck both halves of OwnerGate around every asynchronous queue step.
 * Unmounting OwnerScopedRuntime stops future triggers, but an upload or
 * transcription that was already awaiting the network can outlive sign-out.
 * It may continue only while the same authenticated owner still matches the
 * durable one-account-per-browser binding.
 */
async function ownerGateStillOpen(expectedOwnerId: string): Promise<boolean> {
  try {
    return (
      (await getCurrentUserId()) === expectedOwnerId &&
      (await getContentOwnerBinding()) === expectedOwnerId
    );
  } catch {
    return false;
  }
}

/**
 * V.A.2: upload an audio Blob to Supabase Storage at
 * `${userId}/${queueItemId}.${ext}` in the `prayer-audio` bucket.
 * Returns the full storage path on success. Throws on any failure
 * (network, RLS, quota) — caller decides whether to fall back to
 * the legacy blob-in-IDB path.
 *
 * The Supabase client SDK sends the Blob through the exact signed-upload URL;
 * bytes run client→Storage, never through Vercel
 * functions, so the 4.5 MB function body cap doesn't apply. xAI
 * docs cap at 500 MB, our local cap is 25 MB (matches the v2
 * endpoint's MAX_BYTES).
 *
 * Capability gate (migration 044 + `/api/audio/registry`): the server derives
 * `${ownerId}/${queueId}.${ext}` and signs only that create-only path after
 * authenticating the owner. Anonymous users route through browser STT.
 *
 * `addRandomSuffix` is intentionally NOT used. Queue ids are already
 * UUIDs so collisions don't happen; preserving the exact path means
 * `dequeueAudio` and `discardFailedAudio` can delete the same key
 * without first re-reading the row to discover the suffixed name.
 */
export async function uploadAudioToStorage(
  blob: Blob,
  queueItemId: string,
  userId: string,
  ext: string,
  listFence: QueueFence = captureQueueListFence(),
  provedCopy?: db.VerifiedCaptureCopy,
): Promise<string> {
  assertCoreWriteAllowed('audio:upload');
  if (!enhancedVoiceAllowed()) {
    throw new Error('uploadAudioToStorage: enhanced voice disabled');
  }
  const sb = getSupabase();
  if (!sb) throw new Error('uploadAudioToStorage: supabase client unavailable');
  // Belt-and-braces: assert the userId carries the UUID shape RLS expects.
  // A malformed userId would 403 server-side (good fail-closed posture)
  // but failing fast here gives a cleaner error surface for ops triage.
  if (!UUID.test(userId)) {
    throw new Error('uploadAudioToStorage: malformed userId');
  }
  if (!UUID_V4.test(queueItemId)) {
    throw new Error('uploadAudioToStorage: malformed queueItemId');
  }
  if (!AUDIO_EXTENSION_ALLOWLIST.has(ext.toLowerCase())) {
    throw new Error('uploadAudioToStorage: unsupported extension');
  }
  const timing = createVoiceTiming('upload');
  try {
    const mayContinue = () => captureMayContinue(queueItemId);
    assertCaptureMayContinue(queueItemId);
    const row = await db.getAudioQueueRow(queueItemId);
    if (
      !row ||
      row.ownerId !== userId ||
      !row.blob ||
      row.blob.size !== blob.size ||
      row.mimeType !== (blob.type || row.mimeType)
    ) {
      throw new Error('uploadAudioToStorage: durable row changed');
    }
    // KR8 (db.ts "Verified capture copies"): read and prove the row's bytes
    // before the registration rewrites this row. The verified copy is what is
    // hashed, stored by every rewrite here and uploaded; a Blob read before a
    // rewrite can lose its file in WebKit and is never sent. Bytes that cannot be
    // read or verified stop here with nothing written or sent.
    // A caller that already proved this row's copy for this attempt (processOne,
    // A2) passes it, so the bytes are read once per attempt.
    timing?.start('verify_copy');
    const materialized: db.VerifiedCaptureCopyResult = provedCopy && provedCopy.rowId === queueItemId
      ? { status: 'verified', copy: provedCopy }
      : await db.materializeVerifiedCaptureCopy(queueItemId, userId);
    timing?.end('verify_copy');
    if (materialized.status === 'unverified') {
      throw new db.CaptureBytesUnverifiedError(materialized.reason);
    }
    let verified = materialized.status === 'verified' ? materialized.copy : null;
    assertCaptureMayContinue(queueItemId);
    // A recording made before v0.2.0 with no proof kept yet (db.ts "Recordings
    // made before v0.2.0"): when the row was registered before this upload, the
    // registration answer replays that record and is kept as its proof, so its
    // rewrites prove copies themselves (no copy is passed), and the bytes are
    // proved for the upload body only after it. A first registration keeps no
    // proof: no copy, and the body is the caller's Blob, as before. Bytes that do
    // not match the record are never sent.
    const legacyUnproved = materialized.status === 'not-applicable';
    // Such a recording's bytes are read once, now, before this operation's first
    // rewrite of its row (LC2, decision 2): in WebKit that rewrite can leave every
    // Blob read before it unreadable in this page, which stalled the upload of a
    // first registration. The registration uses the digest of exactly these bytes
    // when the row has none, and when no proof is kept they are the upload body,
    // sent from memory: no copy is stored, nothing is claimed verified, and the
    // server's `uploaded` observation (size and SHA-256 against the registered
    // digest) stays the check. Bytes that cannot be read stop here, before any
    // write or request. Bytes that do not match the size or digest written when
    // the recording was first saved stop here too and are never sent (LC4).
    let bytesRead: ArrayBuffer | null = null;
    let bytesReadSha256: string | null = null;
    if (legacyUnproved) {
      const stored = row.blob;
      try {
        bytesRead = await stored.arrayBuffer();
      } catch {
        throw new db.CaptureBytesUnverifiedError('read-failed');
      }
      if (bytesRead.byteLength !== stored.size) throw new db.CaptureBytesUnverifiedError('length-mismatch');
      if (typeof row.sizeBytes === 'number' && row.sizeBytes !== bytesRead.byteLength) {
        throw new db.CaptureBytesUnverifiedError('length-mismatch');
      }
      bytesReadSha256 = await sha256BytesHex(bytesRead);
      if (typeof row.byteSha256 === 'string' && SHA256_HEX.test(row.byteSha256) && row.byteSha256 !== bytesReadSha256) {
        throw new db.CaptureBytesUnverifiedError('digest-mismatch');
      }
      assertCaptureMayContinue(queueItemId);
    }
    timing?.start('register');
    const registration = await registerDurableAudio(
      row, listFence, mayContinue, legacyUnproved ? undefined : verified,
      bytesReadSha256 ? { rowId: queueItemId, sha256: bytesReadSha256 } : undefined,
    );
    timing?.end('register');
    assertCaptureMayContinue(queueItemId);
    if (legacyUnproved) {
      const proved = await db.materializeVerifiedCaptureCopy(queueItemId, userId);
      if (proved.status === 'unverified') throw new db.CaptureBytesUnverifiedError(proved.reason);
      if (proved.status === 'verified') verified = proved.copy;
      assertCaptureMayContinue(queueItemId);
    }
    const body = verified
      ? verified.blob
      : bytesRead
        ? new Blob([bytesRead], { type: row.blob.type })
        : blob;
    const path = registration.storagePath;
    if (typeof path !== 'string' || path !== `${userId}/${queueItemId}.${ext}`) {
      throw new Error('uploadAudioToStorage: registry path mismatch');
    }
    if (registration.uploadRequired !== false) {
      if (typeof registration.uploadToken !== 'string' || !registration.uploadToken) {
        throw new Error('uploadAudioToStorage: signed token missing');
      }
      timing?.start('storage_upload');
      const { error } = await sb.storage
        .from(STORAGE_BUCKET)
        .uploadToSignedUrl(path, registration.uploadToken, body, {
          contentType: body.type || `audio/${ext}`,
        });
      // A client-visible upload error is ambiguous: the provider may have
      // committed the exact object before the response was lost. Always ask the
      // registry route to observe size + SHA-256. If the object is absent this
      // throws and the durable Blob remains; if it exists the replay converges.
      timing?.end('storage_upload');
      void error;
      assertCaptureMayContinue(queueItemId);
    }
    timing?.start('confirm_upload');
    const updated = await markRegistryUploaded(
      queueItemId,
      registration.registryRevision,
      listFence,
      mayContinue,
      verified,
    );
    timing?.end('confirm_upload');
    // `markRegistryUploaded` is an observation receipt, not merely a pointer
    // echo. Persist its monotonic state before any caller is allowed to drop the
    // only local Blob.
    if (updated.registryState === 'registered') {
      throw new Error('uploadAudioToStorage: upload unproved');
    }
    return path;
  } finally {
    timing?.finish();
  }
}

/**
 * Retired compatibility export. Browser Storage deletion authority was
 * removed in migration 044; exact terminal registry transitions enqueue the
 * only legitimate provider-deletion work and return durable receipts.
 */
export async function deleteStorageAudio(storagePath: string): Promise<void> {
  // Destructive on bytes the cutover exists to preserve. Its only
  // callers are already gated; these are the final backstops. Enhanced
  // Voice is independent from typed writes, so opening core writes must not
  // make a stale cleanup caller capable of deleting parked audio.
  if (isCoreReadOnly()) return;
  if (!enhancedVoiceAllowed()) return;
  // Retained compatibility export only. Browser Storage DELETE authority was
  // revoked by migration 044; callers must transition an exact registry row.
  void storagePath;
}

/**
 * Tracks per-item retry state separate from the IDB `attemptCount`
 * field — the IDB count is the ground truth (survives reload), this
 * Map is just a guard against double-processing the same item within
 * a single processor run.
 */
const inFlightItems = new Set<string>();

/** Read-only: the processor holds a row mid-upload/transcribe (app-update safety check). */
export function isAudioQueueProcessing(): boolean {
  return inFlightItems.size > 0;
}

// Part R: start and finish of in-flight queue work, and of explicit bookmark
// runs, are observable so a status can end even when a failure writes nothing
// to the row. Listeners receive no data: they re-read under their own owner
// and scope checks, so a stale owner's work can never update another's view.
const audioActivityListeners = new Set<() => void>();

function notifyAudioActivity(): void {
  for (const listener of [...audioActivityListeners]) {
    try {
      listener();
    } catch {
      // Observers only.
    }
  }
}

function markItemInFlight(id: string): void {
  inFlightItems.add(id);
  notifyAudioActivity();
}

function clearItemInFlight(id: string): void {
  if (inFlightItems.delete(id)) notifyAudioActivity();
}

export function subscribeAudioQueueActivity(listener: () => void): () => void {
  audioActivityListeners.add(listener);
  return () => {
    audioActivityListeners.delete(listener);
  };
}

/** Read-only: this page is uploading or transcribing this row right now. */
export function isAudioQueueItemInFlight(id: string): boolean {
  return inFlightItems.has(id) || bookmarkRuns.has(id);
}

/**
 * Hydrate the in-memory zustand mirror from IDB on app boot. Per R12,
 * IDB unavailability (private browsing, disk full, corrupted store)
 * must NOT crash the app — the user can still record via the browser
 * fallback path. We log and bail.
 */
export async function hydrateQueueStore(): Promise<void> {
  // The scope this hydrate belongs to, captured before reading. It fills and
  // marks only that runtime's list; it never opens or reopens a scope, so a
  // hydrate that outlives its runtime changes nothing.
  const listFence = captureQueueListFence();
  if (!listFence) return;
  try {
    const rows = await db.getAllAudioQueueRows();
    const items: QueueItemMeta[] = rows.map(queueMeta);
    queueList().replaceItems(listFence, items);
    if (enhancedVoiceAllowed()) {
      await discoverRegistryAudio(listFence).catch(() => []);
    }
    await noteMissingRecordings(listFence);
    queueList().markHydrated(listFence.runtimeId);
  } catch {
    console.error('[Adia] audio-queue hydrate failed; continuing without queue');
    queueList().markHydrated(listFence.runtimeId, { failed: true });
  }
}

/**
 * LC7 (review D-A): a stored pending Prayer whose recording row is not on this
 * device can never be processed here, so it must not be shown as finishing.
 * Decided after this hydrate's rows (and any rows discovery restored) are in,
 * from one owner-checked IndexedDB read; the owner gate is checked again after
 * it, and each mark applies only under this hydrate's own fence. Never throws:
 * a failed read marks nothing (the prayer keeps its earlier presentation).
 */
async function noteMissingRecordings(listFence: QueueFence): Promise<void> {
  try {
    const ownerId = await getCurrentUserId();
    if (!ownerId || !(await ownerGateStillOpen(ownerId))) return;
    const missing = await db.pendingPrayerRecordingsMissing(ownerId);
    if (missing.length === 0 || !(await ownerGateStillOpen(ownerId))) return;
    for (const queueId of missing) queueList().setAttention(listFence, queueId, 'missing');
  } catch {
    console.error('[Adia] audio-queue missing-recording check failed');
  }
}

/**
 * Persist a new captured-audio entry. Always succeeds even if the
 * queue is over cap (R10) — the user's already-captured audio takes
 * priority. The cap gates new recording attempts at the MicButton
 * surface, not enqueue itself.
 *
 * V.A.2: caller passes EITHER `storagePath` (audio uploaded
 * client-side and now lives in Storage) OR `blob` (upload failed,
 * legacy pre-V.A.2 path, or anonymous user — processOne migrates
 * blob→storage on the next pass). Invariant enforced here: at least
 * one of the two must be set, or enqueueAudio throws.
 *
 * `sizeBytes` is required so the zustand mirror's deriveCounters
 * stays honest after the blob is dropped post-migration. Caller
 * passes `blob.size` (or the equivalent if reconstructing).
 *
 * Returns the persisted row's id (matches the input `id`) for the
 * caller to link via Prayer.pendingTranscriptionId.
 */
export async function enqueueAudio(input: {
  id: string;
  prayerId: string | null;
  /** Bind late recorder work to the owner that started the capture. */
  expectedOwnerId?: string;
  /** Owner/auth snapshot captured before the recorder's first await. */
  authEpochAtCapture?: number;
  /** Actual recorder path after fallback selection. */
  captureMode?: AudioCaptureMode;
  /** Stable destination identity minted for this capture. */
  destinationId?: string;
  sourceDeviceId?: string;
  /** V.A.2: optional. Required when storagePath is absent. */
  blob?: Blob;
  /** V.A.2: optional. Required when blob is absent. */
  storagePath?: string;
  /** V.A.2: required so the meta mirror has a size after blob is dropped. */
  sizeBytes: number;
  mimeType: string;
  language: string;
  /**
   * V.1.1: pass true from useSpeechToText's onstop-catch so the row
   * is excluded from both the resume card and the processor's auto-
   * retry loop. PrayPage's Save claims the row and calls
   * createPendingPrayerFromAudio, which clears this flag atomically.
   */
  awaitingInlineSave?: boolean;
  /**
   * Source surface that produced this orphan (see normalizeSource).
   * Absent defaults to 'prayer' at read time. Bookmark recordings pass
   * 'bookmark' so the orphan is never claimable as a prayer.
   */
  inlineSaveSource?: 'prayer' | 'bookmark';
  /** Exact frozen passage for bookmark-source recovery. */
  scriptureAnchor?: ScriptureAnchor;
  recoveryKind?: AudioRecoveryKind;
  /** Browser interruption may already have text and no retained audio bytes. */
  transcribedText?: string;
  /** Owner transition parks bytes only; the old owner must resume processing. */
  deferProcessing?: boolean;
}): Promise<string> {
  assertCoreWriteAllowed('audio:enqueue');
  const listFence = captureQueueListFence();
  if (!input.blob && !input.storagePath && !input.transcribedText?.trim()) {
    throw new Error('enqueueAudio: requires audio bytes or recovered text');
  }
  // IDB is an untrusted persistence boundary. Keep the recovery row and its
  // words/audio even if optional provenance is malformed, but omit that
  // provenance so no Bible surface can silently claim it for a wrong passage.
  const scriptureAnchor = input.scriptureAnchor
    ? parseScriptureAnchor(input.scriptureAnchor) ?? undefined
    : undefined;
  const hasAuthoritativeEnvelope = Boolean(
    input.expectedOwnerId &&
    typeof input.authEpochAtCapture === 'number' &&
    input.captureMode &&
    input.destinationId &&
    input.sourceDeviceId,
  );
  const entryFenceStillCurrent =
    hasAuthoritativeEnvelope &&
    getAuthUserId() === input.expectedOwnerId &&
    getAuthEpoch() === input.authEpochAtCapture;
  const row: db.AudioQueueRow = {
    id: input.id,
    ownerId: input.expectedOwnerId,
    authEpochAtCapture: input.authEpochAtCapture,
    audioProtocolVersion: hasAuthoritativeEnvelope ? 1 : undefined,
    captureMode: input.captureMode ?? 'legacy-unknown',
    destinationId: input.destinationId,
    sourceDeviceId: input.sourceDeviceId,
    prayerId: input.prayerId,
    blob: input.blob,
    storagePath: input.storagePath,
    sizeBytes: input.sizeBytes,
    mimeType: input.mimeType,
    capturedAt: new Date().toISOString(),
    language: input.language,
    transcribedText: input.transcribedText?.trim() || null,
    attemptCount: 0,
    failed: false,
    awaitingInlineSave: input.awaitingInlineSave,
    inlineSaveSource: input.inlineSaveSource,
    scriptureAnchor,
    recoveryKind: input.recoveryKind ?? 'unknown',
    localDisposition: entryFenceStillCurrent ? 'unresolved' : 'quarantined',
  };
  await db.saveAudioQueueRow(row, input.expectedOwnerId);
  // Only the in-memory mirror is fenced; the durable row above is unchanged.
  queueList().upsertItem(listFence, queueMeta(row));
  // Kick off processing — fire-and-forget, single-flight gated
  // internally so concurrent enqueues don't spawn parallel runs.
  if (!input.deferProcessing) processQueueInBackground();
  return input.id;
}

/**
 * Voice-capture custody's only write path (account-change recording plan rev
 * 4.1, section 4.2): create the capture row if absent, or verify the exact
 * existing capture, through `db.commitCaptureRow` with the current auth reader.
 * The row's `capturedAt` is the custody's admission time. A retired capture
 * is never published to the in-memory list and never starts processing.
 */
export async function commitCustodyCapture(input: {
  row: db.AudioQueueRow;
  claimant: db.CaptureClaimant;
  /** Leave processing to the owner's later queue pass (retired or parked). */
  deferProcessing: boolean;
  publishToList: boolean;
}): Promise<db.CaptureCommitResult> {
  assertCoreWriteAllowed('audio:commit-capture');
  const listFence = captureQueueListFence();
  const ownerId = input.row.ownerId;
  if (!ownerId) throw new db.CaptureCommitError('capture-input-invalid', 'owner');
  const result = await db.commitCaptureRow(
    { row: input.row, claimant: input.claimant },
    ownerId,
    () => ({ userId: getAuthUserId(), epoch: getAuthEpoch() }),
  );
  if (input.publishToList) queueList().upsertItem(listFence, queueMeta(result.row));
  if (!input.deferProcessing) processQueueInBackground();
  return result;
}

/** Persist the Storage pointer before allowing the inline caller to transcribe. */
export async function persistUploadedAudioPointer(
  id: string,
  storagePath: string,
  expectedOwnerId: string,
): Promise<void> {
  const listFence = captureQueueListFence();
  assertCaptureMayContinue(id);
  let refused = false;
  const updated = await db.updateAudioQueueRowIfPresent(
    id,
    expectedOwnerId,
    (row) => {
      if (!captureMayContinue(id)) {
        refused = true;
        return null;
      }
      return row.storagePath === storagePath &&
        row.registryState !== undefined &&
        UPLOAD_PROVED_REGISTRY_STATES.has(row.registryState)
        ? { ...row, blob: undefined }
        : null;
    },
    // Releases the Blob: nothing to copy (KR8).
    null,
  );
  if (refused) throw new Error(CAPTURE_RETIRED_ERROR);
  if (!updated) throw new Error('captured-audio-upload-unproved');
  if (captureMayContinue(id)) queueList().updateItem(listFence, id, { storagePath });
}

/** Persist recovered text before notifying any transient React destination. */
export async function persistInlineTranscription(
  id: string,
  text: string,
  expectedOwnerId: string,
): Promise<void> {
  const listFence = captureQueueListFence();
  const recovered = text.trim();
  if (!recovered) throw new Error('captured-audio-text-empty');
  assertCaptureMayContinue(id);
  let refused = false;
  const updated = await db.updateAudioQueueRowIfPresent(
    id,
    expectedOwnerId,
    (row) => {
      if (!captureMayContinue(id)) {
        refused = true;
        return null;
      }
      return { ...row, transcribedText: recovered };
    },
  );
  if (refused) throw new Error(CAPTURE_RETIRED_ERROR);
  if (!updated) throw new Error('captured-audio-row-missing');
  if (captureMayContinue(id)) queueList().updateItem(listFence, id, { transcribedText: recovered });
}

/** Transcribe only the exact owner-scoped registry row and persist its CAS tip. */
export async function transcribeRegisteredQueueAudio(
  id: string,
  expectedOwnerId: string,
  listFence: QueueFence = captureQueueListFence(),
): Promise<string> {
  const mayContinue = () => captureMayContinue(id);
  assertCaptureMayContinue(id);
  if (!(await ownerGateStillOpen(expectedOwnerId))) {
    throw new Error('registered-audio-owner-changed');
  }
  assertCaptureMayContinue(id);
  let row = await db.getAudioQueueRow(id);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (
      !row ||
      row.ownerId !== expectedOwnerId ||
      row.audioProtocolVersion !== 1 ||
      row.captureMode !== 'enhanced' ||
      !row.storagePath ||
      !row.registryRevision
    ) throw new Error('registered-audio-not-ready');
    const expectedRevision = row.registryRevision;
    try {
      const result = await transcribeAudio({
        queueId: id,
        expectedRegistryRevision: expectedRevision,
        shouldContinue: mayContinue,
      });
      assertCaptureMayContinue(id);
      if (
        !REGISTRY_PROCESSING_STATES.has(result.registryState) ||
        compareRegistryRevision(result.registryRevision, expectedRevision) < 0
      ) throw new Error('registered-audio-response-invalid');
      if (!(await ownerGateStillOpen(expectedOwnerId))) {
        throw new Error('registered-audio-owner-changed');
      }
      assertCaptureMayContinue(id);
      let refused = false;
      const updated = await db.updateAudioQueueRowIfPresent(
        id,
        expectedOwnerId,
        (current) => {
          if (!captureMayContinue(id)) {
            refused = true;
            return null;
          }
          if (!current.registryRevision) return null;
          if (compareRegistryRevision(current.registryRevision, result.registryRevision) > 0) {
            return current;
          }
          return {
            ...current,
            registryRevision: result.registryRevision,
            registryState: result.registryState,
          };
        },
      );
      if (refused) throw new Error(CAPTURE_RETIRED_ERROR);
      if (!updated) throw new Error('registered-audio-revision-changed');
      if (captureMayContinue(id)) {
        queueList().updateItem(listFence, id, {
          registryRevision: updated.registryRevision,
          registryState: updated.registryState,
          confirmedNoSpeechRevision: undefined,
        });
      }
      return result.text.trim();
    } catch (error) {
      if (!(error instanceof ParseError)) throw error;
      assertCaptureMayContinue(id);
      const revision = error.registryRevision;
      const state = error.registryState;
      if (revision && state) {
        if (
          !REGISTRY_PROCESSING_STATES.has(state) ||
          compareRegistryRevision(revision, expectedRevision) < 0
        ) throw new Error('registered-audio-error-response-invalid');
        let refused = false;
        const updated = await db.updateAudioQueueRowIfPresent(
          id,
          expectedOwnerId,
          (current) => {
            if (!captureMayContinue(id)) {
              refused = true;
              return null;
            }
            if (
              current.registryRevision &&
              compareRegistryRevision(current.registryRevision, revision) > 0
            ) return current;
            return { ...current, registryRevision: revision, registryState: state };
          },
        );
        if (refused) throw new Error(CAPTURE_RETIRED_ERROR);
        if (!updated) throw new Error('registered-audio-revision-changed');
        if (captureMayContinue(id)) {
          queueList().updateItem(listFence, id, {
            registryRevision: updated.registryRevision,
            registryState: updated.registryState,
            confirmedNoSpeechRevision:
              error.serverCode === 'No speech detected' && state === 'failed' &&
              updated.registryRevision === revision && updated.registryState === state
                ? revision : undefined,
          });
        }
        row = updated;
      }
      if (error.serverCode === 'revision_conflict' && revision && attempt === 0) {
        continue;
      }
      throw error;
    }
  }
  throw new Error('registered-audio-transcribe-retry-exhausted');
}

/**
 * Hold the same cross-document lock used by the background worker while an
 * inline capture owns upload/transcription. The row is written inside the
 * claim, so another tab can never observe it before the claim exists.
 */
export async function runInlineAudioExclusive<T>(
  id: string,
  expectedOwnerId: string,
  task: () => Promise<T>,
): Promise<T> {
  const run = async (): Promise<T> => {
    markItemInFlight(id);
    try {
      return await task();
    } finally {
      clearItemInFlight(id);
      // A refused continuation (a retired capture, or one whose owner or
      // epoch changed) starts no queue pass on its way out: its durable row
      // waits for the owner's next pass (mount, online or visible), as a
      // retired capture's commit already does. The caller finishes the
      // capture's pipeline after this returns, so custody still knows it.
      if (captureMayContinue(id)) processQueueInBackground();
    }
  };
  if (typeof navigator === 'undefined' || !navigator.locks) return run();
  return navigator.locks.request(
    `adia-audio:${expectedOwnerId}:${id}`,
    { mode: 'exclusive' },
    async () => run(),
  );
}

/**
 * Decide whether a queue row may be physically removed under the current
 * Enhanced Voice posture. A row without durable text may still contain the
 * only audio copy of a prayer, so it stays fail-closed while cloud voice is
 * disabled. Text-ready rows remain locally cleanable only when they carry no
 * Blob or Storage pointer; otherwise deleting the row would orphan preserved
 * audio from its only local index. An unreadable/missing row is unknown
 * content and therefore also stays protected while voice is off.
 *
 * `voiceEnabled` is explicit so the behavior can be exhaustively tested as a
 * pure policy. Runtime callers must pass `enhancedVoiceAllowed()`.
 */
export function audioQueueDeletionAllowed(
  row: Pick<
    db.AudioQueueRow,
    | 'blob'
    | 'storagePath'
    | 'transcribedText'
    | 'ownerId'
    | 'audioProtocolVersion'
    | 'captureMode'
    | 'destinationId'
    | 'recoveryKind'
    | 'localDisposition'
  > | undefined,
  voiceEnabled: boolean,
): boolean {
  if (
    !row ||
    !row.ownerId ||
    row.audioProtocolVersion !== 1 ||
    (row.captureMode !== 'enhanced' && row.captureMode !== 'browser') ||
    !row.destinationId ||
    normalizeRecoveryKind(row.recoveryKind) === 'unknown' ||
    row.localDisposition === 'quarantined' ||
    row.localDisposition === 'deleted'
  ) return false;
  return voiceEnabled || db.audioQueueRowIsTextOnly(row);
}

/**
 * A Save's content commit already took this row: the commit's claim binds it
 * (`destination-commit-pending` with the exact destination operation) and only
 * `finalizeCommittedAudio`, after the content receipt, may release it. Such a
 * row is the Save's, never a discard's.
 */
function rowConsumedBySave(row: db.AudioQueueRow): boolean {
  return row.localDisposition === 'destination-commit-pending' || row.destinationOpId != null;
}

/**
 * Whether an explicit discard may still take this row: this owner's row, not
 * consumed by a Save, not quarantined and not deleted. An unresolved row and a
 * row whose discard was already decided (`discard-pending`, retried by the
 * queue pass) qualify. `dequeueAudio` repeats this inside the owner-checked
 * transaction that records the discard, before any server effect.
 */
function discardMayTake(row: db.AudioQueueRow, ownerId: string): boolean {
  return row.ownerId === ownerId &&
    !rowConsumedBySave(row) &&
    row.localDisposition !== 'quarantined' &&
    row.localDisposition !== 'deleted';
}

/** The awaiting-inline-save recording of one surface, as the discard helpers select it. */
function awaitingInlineSaveFrom(row: db.AudioQueueRow, source: 'prayer' | 'bookmark'): boolean {
  return Boolean(row.awaitingInlineSave) && normalizeSource(row.inlineSaveSource) === source;
}

/**
 * Whether this page's mirror item is an inline-save offer of `source`:
 * awaiting an inline save, unresolved, and with no Scripture anchor when
 * `expectedAnchor` is null (Home) or an equal validated anchor otherwise.
 * The one selection behind the Save's claim (useVoiceCapture
 * claimPendingInlineSave), Home's rescue banner and its Discard.
 */
export function inlineSaveOffered(
  item: QueueItemMeta,
  source: 'prayer' | 'bookmark',
  expectedAnchor: ScriptureAnchor | null,
): boolean {
  if (
    !item.awaitingInlineSave ||
    item.localDisposition !== 'unresolved' ||
    (item.destinationState !== undefined && item.destinationState !== 'unresolved') ||
    normalizeSource(item.inlineSaveSource) !== source
  ) return false;
  if (!expectedAnchor) return item.scriptureAnchor === undefined;
  if (item.scriptureAnchor === undefined) return false;
  const rowAnchor = parseScriptureAnchor(item.scriptureAnchor);
  return Boolean(rowAnchor && scriptureAnchorsEqual(rowAnchor, expectedAnchor));
}

/** The newest inline-save offer of `source` in this page's mirror, the recording a Save claims and a Discard means. */
export function newestInlineSaveOffer(
  items: readonly QueueItemMeta[],
  source: 'prayer' | 'bookmark',
  expectedAnchor: ScriptureAnchor | null,
): QueueItemMeta | null {
  return items
    .filter((item) => inlineSaveOffered(item, source, expectedAnchor))
    .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))[0] ?? null;
}

/**
 * RU item 2 (2026-10-20): the one recording Home offers, the one its Save
 * claims (when the offer, not a recording just made on Home, is why Save is
 * shown) and the one its Discard removes: the newest prayer-source inline-save
 * offer in this page's owner-scoped mirror, linked to no prayer, that has no Bible passage or a
 * passage that validates. A recording made on a passage (Pray on Scripture,
 * reached nowhere else once its sheet closes) is returned with that validated
 * passage: Home's Save creates the pending prayer with it, and
 * db.savePendingPrayerAndLinkAudio checks the two are equal again inside its
 * transaction. A stored passage that does not validate is offered nowhere
 * (rule #89), and a journal (bookmark) recording is never a prayer offer.
 * Selection only: no row is read or written.
 */
export function homeInlineSaveOffer(
  items: readonly QueueItemMeta[],
): { item: QueueItemMeta; scriptureAnchor: ScriptureAnchor | null } | null {
  let head: { item: QueueItemMeta; scriptureAnchor: ScriptureAnchor | null } | null = null;
  for (const item of items) {
    if (normalizeSource(item.inlineSaveSource) !== 'prayer' || Boolean(item.prayerId)) continue;
    const scriptureAnchor = item.scriptureAnchor === undefined ? null : parseScriptureAnchor(item.scriptureAnchor);
    if (item.scriptureAnchor !== undefined && !scriptureAnchor) continue;
    if (!inlineSaveOffered(item, 'prayer', scriptureAnchor)) continue;
    // Newest first; on equal capture times the earlier item stays, as in newestInlineSaveOffer.
    if (!head || item.capturedAt.localeCompare(head.item.capturedAt) > 0) head = { item, scriptureAnchor };
  }
  return head;
}

/** A terminal registry failure cannot become a permanently empty prayer. */
export function inlineSaveOfferFailedWithoutWords(
  item: Pick<QueueItemMeta, 'registryState' | 'transcribedText'>,
): boolean {
  return item.registryState === 'failed' && !item.transcribedText?.trim();
}

/**
 * `failed` alone does not prove silence: download failures also use it. Only
 * this runtime's explicit no-speech reply for the same revision can say so.
 * Reload/discovery without that evidence uses generic failure copy.
 */
export function inlineSaveOfferHeardNoWords(
  item: Pick<QueueItemMeta, 'registryState' | 'transcribedText' | 'registryRevision' | 'confirmedNoSpeechRevision'>,
): boolean {
  return inlineSaveOfferFailedWithoutWords(item) &&
    item.confirmedNoSpeechRevision !== undefined &&
    item.confirmedNoSpeechRevision === item.registryRevision;
}

/**
 * Whether a durable row is still the awaiting inline-save recording a surface
 * offered: awaiting, of that source, unresolved, linked to no Prayer and not
 * consumed by a Save. Otherwise it is no longer that surface's to discard.
 */
function inlineSaveRowStillOffered(row: db.AudioQueueRow, source: 'prayer' | 'bookmark'): boolean {
  return awaitingInlineSaveFrom(row, source) &&
    row.prayerId == null &&
    row.localDisposition === 'unresolved' &&
    (row.destinationState === undefined || row.destinationState === 'unresolved') &&
    !rowConsumedBySave(row);
}

/**
 * Whether a durable row is still the interrupted recording the resume card
 * offered (useVoiceCapture interruptedSessions): a new-prayer recording, linked
 * to no Prayer, not awaiting an inline save, unresolved.
 */
export function interruptedRecordingStillOffered(row: db.AudioQueueRow): boolean {
  return row.prayerId == null &&
    !row.awaitingInlineSave &&
    row.recoveryKind === 'new-prayer' &&
    row.localDisposition === 'unresolved' &&
    (row.destinationState === undefined || row.destinationState === 'unresolved') &&
    !rowConsumedBySave(row);
}

/**
 * Bring this page's mirror item up to its durable row (in memory only), so an
 * offer or card for a recording that is no longer offered leaves. Other
 * owners' rows are left as they are.
 */
function publishRowState(row: db.AudioQueueRow, ownerId: string, listFence: QueueFence): void {
  if (row.ownerId !== ownerId) return;
  queueList().updateItem(listFence, row.id, queueMeta(row));
}

/**
 * Publish a row a Save's commit bound to this page's queue mirror (in memory
 * only), so an inline-save offer for it leaves. Only this owner's
 * `destination-commit-pending` rows are published.
 */
function publishCommittedRow(row: db.AudioQueueRow, ownerId: string, listFence: QueueFence): void {
  if (row.ownerId !== ownerId || row.localDisposition !== 'destination-commit-pending') return;
  queueList().updateItem(listFence, row.id, {
    prayerId: row.prayerId,
    awaitingInlineSave: row.awaitingInlineSave,
    localDisposition: row.localDisposition,
    destinationEntityType: row.destinationEntityType,
    destinationEntityId: row.destinationEntityId,
    destinationOpId: row.destinationOpId,
  });
}

/**
 * Publish the durable state of a row no Save may claim any more to this page's
 * mirror (in memory only), so an offer for it leaves: a row a Save consumed
 * (`publishCommittedRow`) or one whose discard is recorded (`discard-pending`,
 * possibly by another page). Every offer requires `unresolved`. Other rows,
 * and other owners' rows, are left as they are.
 */
function publishUnclaimableRow(row: db.AudioQueueRow, ownerId: string, listFence: QueueFence): void {
  if (row.ownerId !== ownerId) return;
  if (row.localDisposition === 'destination-commit-pending') {
    publishCommittedRow(row, ownerId, listFence);
    return;
  }
  if (row.localDisposition !== 'discard-pending') return;
  queueList().updateItem(listFence, row.id, {
    awaitingInlineSave: row.awaitingInlineSave,
    localDisposition: row.localDisposition,
    destinationState: row.destinationState,
  });
}

/**
 * Remove this page's mirror item for a recording whose durable row no longer
 * exists (another page discarded or released it), so a stale offer or card
 * leaves. In memory only: IndexedDB is read, never written. Requires the owner
 * gate for `ownerId`, a mirror item of that owner, and the row still absent on
 * a reread by id. Every mirror item is published only after its durable write
 * (enqueue, capture custody, discovery and hydrate all write or read first), so
 * an absent row means a removed one. Never rejects; returns whether it removed.
 */
async function forgetMissingRow(id: string, ownerId: string, listFence: QueueFence): Promise<boolean> {
  try {
    const item = queueList().items.find((candidate) => candidate.id === id);
    if (!item || item.ownerId !== ownerId) return false;
    if (!(await ownerGateStillOpen(ownerId))) return false;
    if ((await db.getAudioQueueRow(id)) !== undefined) return false;
    queueList().removeItem(listFence, id);
    return true;
  } catch {
    return false;
  }
}

/**
 * The mirror items an inline-save offer of `source` shows, whose durable rows
 * are missing from `rows` (a snapshot just read): each is reread by id and
 * removed only while still absent (`forgetMissingRow`).
 */
async function forgetMissingInlineSaveItems(
  rows: readonly db.AudioQueueRow[],
  source: 'prayer' | 'bookmark',
  ownerId: string,
  listFence: QueueFence,
): Promise<void> {
  const durable = new Set(rows.map((row) => row.id));
  const stale = queueList().items.filter((item) =>
    Boolean(item.awaitingInlineSave) &&
    normalizeSource(item.inlineSaveSource) === source &&
    !durable.has(item.id));
  for (const item of stale) await forgetMissingRow(item.id, ownerId, listFence);
}

function localDeletionAuthority(
  row: db.AudioQueueRow,
  kind: 'content-receipt' | 'explicit-browser-discard',
): db.AudioQueueDeletionAuthority {
  return {
    kind,
    receiptId: kind === 'content-receipt' ? row.destinationOpId ?? null : null,
    expectedRegistryRevision: row.registryRevision ?? null,
    expectedDestinationOpId: row.destinationOpId ?? null,
    expectedDestinationEntityType: row.destinationEntityType ?? null,
    expectedDestinationEntityId: row.destinationEntityId ?? null,
    expectedDestinationSubId: row.destinationSubId ?? null,
  };
}

/**
 * Read the durable row before an update/testimony hook claims permission to
 * consume it. The lifecycle transaction repeats this decision at commit time;
 * this preflight exists so a voice-off typed save can simply omit a byte-
 * bearing claim and still commit the user's text.
 */
export async function recoveredAudioClaimAllowed(queueItemId: string): Promise<boolean> {
  try {
    const row = await db.getAudioQueueRow(queueItemId);
    const ownerId = getAuthUserId();
    return row?.ownerId === ownerId &&
      audioQueueDeletionAllowed(row, enhancedVoiceAllowed());
  } catch {
    return false;
  }
}

/**
 * Make an exact pending new-prayer row durably inert before typed recovery
 * clears the Prayer's pending pointer. This never removes IDB bytes or its
 * Storage path. Missing rows are tolerated so typing can rescue a Prayer
 * whose audio was already absent; mismatched existing rows fail closed.
 */
export async function parkTypedRecoveryAudio<T>(
  queueItemId: string,
  prayerId: string,
  commitTypedPrayer: (
    durableBase?: Prayer,
    expectedAuth?: ContentLifecycleAuthExpectation,
  ) => Promise<T>,
  // LC4: a recording whose bytes do not match their reference can never be
  // sent, so its typed recovery keeps it exactly as the voice-off path does,
  // even while Enhanced Voice is on (the row is made terminal and stays kept).
  options?: { keepRecording?: boolean },
): Promise<T> {
  assertCoreWriteAllowed('audio:park-typed-recovery');
  if (enhancedVoiceAllowed() && !options?.keepRecording) return commitTypedPrayer();
  const listFence = captureQueueListFence();
  // Capture both values synchronously before the first await. Owner id alone
  // cannot distinguish A -> B -> A in the same document; the epoch can.
  const authOwnerAtEntry = getAuthUserId();
  const authEpochAtEntry = getAuthEpoch();
  const expectedOwnerId = await getCurrentUserId();
  const authSnapshotIsCurrent = (): boolean =>
    getAuthUserId() === authOwnerAtEntry &&
    getAuthEpoch() === authEpochAtEntry;
  if (
    typeof authOwnerAtEntry !== 'string' ||
    expectedOwnerId !== authOwnerAtEntry ||
    !authSnapshotIsCurrent() ||
    !(await ownerGateStillOpen(expectedOwnerId)) ||
    !authSnapshotIsCurrent()
  ) {
    throw new Error('parkTypedRecoveryAudio: owner changed');
  }
  return runInlineAudioExclusive(queueItemId, expectedOwnerId, async () => {
    const commitIfOwnerCurrent = async (durableBase?: Prayer): Promise<T> => {
      if (
        !authSnapshotIsCurrent() ||
        !(await ownerGateStillOpen(expectedOwnerId)) ||
        !authSnapshotIsCurrent()
      ) {
        throw new Error('parkTypedRecoveryAudio: owner changed');
      }
      return commitTypedPrayer(durableBase, {
        ownerId: expectedOwnerId,
        authEpoch: authEpochAtEntry,
      });
    };
    if (
      !authSnapshotIsCurrent() ||
      !(await ownerGateStillOpen(expectedOwnerId)) ||
      !authSnapshotIsCurrent()
    ) {
      throw new Error('parkTypedRecoveryAudio: owner changed');
    }
    // Reuse the established multi-store transaction: owner binding, exact
    // Prayer.pendingTranscriptionId, Prayer recovery visibility, and queue
    // terminal state are validated/committed together while holding the same
    // owner/id Web Lock as the processor. If the later typed Prayer update
    // fails, a future voice-enabled build still renders the recovery banner.
    let parked: Awaited<ReturnType<typeof db.failPendingPrayerTranscription>>;
    try {
      parked = await db.failPendingPrayerTranscription(
        prayerId,
        queueItemId,
        expectedOwnerId,
        MAX_QUEUE_ATTEMPTS,
      );
    } catch (error) {
      // D3 (LC5; the person's words are saved reliably): the latch write was
      // refused only because this recording's own bytes cannot be read in this
      // page (db.ts `refuseUnreadableRecordingWrite`). When the pair is already
      // terminal, it is already processor-inert and kept, so the latch would
      // change nothing that matters: the words are committed and the recording
      // is not written at all. A pair that is not terminal stays refused (it
      // cannot be made inert here without that write).
      if (!recordingBytesUnreadable(error)) throw error;
      const terminalBase = await alreadyTerminalPair(queueItemId, prayerId, expectedOwnerId);
      if (!terminalBase) throw error;
      return commitIfOwnerCurrent(terminalBase);
    }
    if (!parked) {
      const [current, durablePrayer] = await Promise.all([
        db.getAudioQueueRow(queueItemId),
        db.getPrayer(prayerId),
      ]);
      // A genuinely missing audio row may still be rescued by typing, but
      // only while the durable Prayer continues to name this exact missing
      // queue id. If another tab completed/deleted it and advanced the Prayer,
      // fail closed instead of rebuilding from a stale Zustand projection and
      // overwriting the recovered transcript.
      if (!current && durablePrayer?.pendingTranscriptionId === queueItemId) {
        return commitIfOwnerCurrent(durablePrayer);
      }
      throw new Error('parkTypedRecoveryAudio: recovery state changed');
    }
    if (
      !authSnapshotIsCurrent() ||
      !(await ownerGateStillOpen(expectedOwnerId)) ||
      !authSnapshotIsCurrent()
    ) {
      throw new Error('parkTypedRecoveryAudio: owner changed');
    }
    useAdiaStore.setState((state) => ({
      prayers: state.prayers.map((prayer) =>
        prayer.id === parked.prayer.id ? parked.prayer : prayer),
    }));
    queueList().updateItem(listFence, queueItemId, {
      attemptCount: parked.row.attemptCount,
      failed: true,
    });
    // Hold the audio lock until the typed Prayer write clears its pending
    // pointer. A failed write leaves the atomic failure state above intact;
    // a successful write leaves the row terminal and byte-preserved.
    return commitIfOwnerCurrent(parked.prayer);
  });
}

/**
 * D3 (LC5): the durable Prayer when its new-prayer pair is already terminal for
 * this owner (the Prayer still names the row and carries its failure flag; the
 * row names the Prayer and is failed); otherwise null. Reads the row, never its
 * bytes.
 */
async function alreadyTerminalPair(
  queueItemId: string,
  prayerId: string,
  expectedOwnerId: string,
): Promise<Prayer | null> {
  const [row, prayer] = await Promise.all([db.getAudioQueueRow(queueItemId), db.getPrayer(prayerId)]);
  return row && prayer &&
    row.ownerId === expectedOwnerId &&
    row.prayerId === prayerId &&
    // The same row kinds the terminal latch accepts (db.failPendingPrayerTranscription).
    (row.recoveryKind == null || row.recoveryKind === 'new-prayer') &&
    row.failed === true &&
    prayer.pendingTranscriptionId === queueItemId &&
    prayer.transcriptionFailed === true
    ? prayer
    : null;
}

/**
 * D3 (LC5): whether a typed recovery Save was refused only because its claimed
 * recording's own bytes cannot be read in this page (and not, for example,
 * because another recording bound to the same operation cannot). The editor
 * then commits the person's words through `parkTypedRecoveryAudio` with
 * `keepRecording`, which never consumes the recording.
 */
export async function typedRecoveryRefusedOnlyForUnreadableRecording(
  error: unknown,
  queueItemId: string,
): Promise<boolean> {
  if (!recordingBytesUnreadable(error)) return false;
  try {
    return !(await db.storedRecordingBytesReadable(queueItemId));
  } catch {
    return false;
  }
}

/**
 * LC7 (review D-B): whether a Save was refused because a recording it binds
 * cannot be read in this page. In a Save that error comes only from the byte
 * read made before its transaction for a recording it claims or rebinds (db.ts
 * `refuseUnreadableRecordingWrite`); trying again in the same page cannot
 * succeed, so the surface says to copy the words, close and reopen the app and
 * save again. Any other failure keeps its own copy.
 */
export function saveRefusedForUnreadableRecording(error: unknown): boolean {
  return recordingBytesUnreadable(error);
}

/**
 * Remove a queue entry from both IDB and the zustand store. Used by
 * the resume card's Discard action and by the success path of
 * processQueue (R7 — audio cleared on transcription success).
 *
 * V.A.2: when the row carries `storagePath`, also fire-and-forget the
 * Storage delete so the bytes don't linger after the row is gone.
 * Read the row BEFORE deleting from IDB so we know which Storage key
 * to remove. The Storage delete is best-effort — if it fails, V.A.4's
 * cleanup sweep is the backstop. Ordering is IDB delete first → store
 * remove → Storage delete: the user-visible state (queue row gone)
 * commits before the network hop, so a slow Storage delete doesn't
 * stall callers.
 *
 * Mutation boundary (2026-10-18): before any server effect, one owner-checked
 * IndexedDB transaction rereads the row and records the discard
 * (`discard-pending`) only while a discard may still take it
 * (`discardMayTake`, the byte veto, and the caller's own selection passed as
 * `admit`). A Save's claim requires `unresolved`, so the Save and the discard
 * exclude each other in IndexedDB, whichever commits first. A row a Save
 * already consumed is refused with no write and no registry call, and its
 * committed state is published to this page's mirror so a stale offer leaves.
 * If the discard then ends with no server answer (the registry call throws,
 * or the browser row's delete is refused), the decision this call recorded is
 * withdrawn by compare, so a failed discard leaves the row as it was, and the
 * restored row is published to this page's mirror (`publishRowState`), so an
 * offer that a refused second Discard hid comes back. An answer without a
 * deletion receipt keeps `discard-pending` for the queue pass, as before, and
 * that write is a compare too. A row that no longer
 * exists only removes this page's stale mirror item (`forgetMissingRow`).
 */
export async function dequeueAudio(
  id: string,
  expectedOwnerId?: string,
  listFence: QueueFence = captureQueueListFence(),
  admit?: (row: db.AudioQueueRow) => boolean,
): Promise<boolean> {
  // Read-only: never drop a row or its Storage object (preservation).
  if (isCoreReadOnly()) return false;
  const voiceEnabled = enhancedVoiceAllowed();
  let row: db.AudioQueueRow | undefined;
  let storagePath: string | undefined;
  let readFailed = false;
  try {
    row = await db.getAudioQueueRow(id);
    storagePath = row?.storagePath;
  } catch {
    // While Enhanced Voice is open, preserve the historical best-effort
    // behavior below. While it is closed, an unreadable row is unidentified
    // protected content and must stay fail-closed.
    readFailed = true;
  }
  if (!row && !readFailed) {
    // Nothing to discard: the recording is already gone (another page
    // discarded or released it). Only this page's stale mirror item leaves.
    const missingOwner = expectedOwnerId ?? await getCurrentUserId();
    if (missingOwner) await forgetMissingRow(id, missingOwner, listFence);
    return false;
  }
  if (!audioQueueDeletionAllowed(row, voiceEnabled)) return false;
  if (!row) return false;
  const ownerId = expectedOwnerId ?? await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) return false;
  if (row?.ownerId !== undefined && row.ownerId !== ownerId) return false;
  // KR8 (db.ts "Verified capture copies"): the decision, its withdrawal and the
  // parked state below each rewrite a recording that may still hold its Blob.
  // Each stores one verified copy of the bytes, read and proved now, before any
  // transaction, so a withdrawn discard leaves the recording readable for the
  // next Save, words step or upload. Bytes whose read fails are not rewritten:
  // the discard is refused before any write (LC2, db.ts
  // `refuseUnreadableRecordingWrite`), and the caller reports that it could not
  // be discarded and is still kept. Bytes that read but cannot be verified get
  // no copy: those rewrites keep the stored Blob, as before.
  const materialized = await db.materializeVerifiedCaptureCopy(id, ownerId);
  try {
    db.refuseUnreadableRecordingWrite(materialized);
  } catch {
    console.error('[Adia] audio-queue dequeue refused: recording bytes unreadable');
    return false;
  }
  const verifiedCopy = materialized.status === 'verified' ? materialized.copy : null;
  const mayTake = (current: db.AudioQueueRow): boolean =>
    discardMayTake(current, ownerId) &&
    audioQueueDeletionAllowed(current, voiceEnabled) &&
    (!admit || admit(current));
  let refused = null as db.AudioQueueRow | null;
  let decidedHere = false as boolean;
  let previousDisposition = undefined as db.AudioQueueRow['localDisposition'];
  let decided: db.AudioQueueRow | null;
  try {
    decided = await db.updateAudioQueueRowIfPresent(id, ownerId, (current) => {
      if (!mayTake(current)) {
        refused = current;
        return null;
      }
      if (current.localDisposition === 'discard-pending') return current;
      decidedHere = true;
      previousDisposition = current.localDisposition;
      return { ...current, localDisposition: 'discard-pending' };
    }, verifiedCopy);
  } catch {
    console.error('[Adia] audio-queue dequeue failed');
    return false;
  }
  if (!decided) {
    // A stale discard of a recording a Save already took, or one the caller
    // no longer offered (`admit`): nothing written, nothing sent; this page's
    // mirror learns the row's state, so a stale offer or card leaves. A row
    // that vanished since the first read leaves the mirror.
    if (refused) publishRowState(refused, ownerId, listFence);
    else await forgetMissingRow(id, ownerId, listFence);
    return false;
  }
  // True once the registry answered the discard (with or without a receipt);
  // after that the recorded decision is never withdrawn.
  let answered = false;
  try {
    let authority: db.AudioQueueDeletionAuthority;
    if (decided.captureMode === 'enhanced') {
      const receipt = await discardRegistryAudio(decided, listFence);
      answered = true;
      if (!receipt) {
        const parked = await db.updateAudioQueueRowIfPresent(id, ownerId, (current) => {
          if (!discardMayTake(current, ownerId)) return null;
          return current.localDisposition === 'discard-pending'
            ? current
            : { ...current, localDisposition: 'discard-pending' };
        }, verifiedCopy);
        if (parked) queueList().updateItem(listFence, id, {
          localDisposition: 'discard-pending',
        });
        return false;
      }
      authority = receipt;
    } else {
      authority = localDeletionAuthority(decided, 'explicit-browser-discard');
    }
    const deleted = await db.deleteAudioQueueRow(id, ownerId, authority);
    if (!deleted) {
      if (decidedHere && !answered) {
        const restored = await withdrawDiscardDecision(id, ownerId, previousDisposition, verifiedCopy);
        if (restored) publishRowState(restored, ownerId, listFence);
      }
      return false;
    }
  } catch {
    console.error('[Adia] audio-queue dequeue failed');
    if (decidedHere && !answered) {
      const restored = await withdrawDiscardDecision(id, ownerId, previousDisposition, verifiedCopy);
      if (restored) publishRowState(restored, ownerId, listFence);
    }
    return false;
  }
  queueList().removeItem(listFence, id);
  void storagePath;
  return true;
}

/**
 * Withdraw the discard decision `dequeueAudio` recorded when that discard
 * ended with no server answer, so the row returns to its previous disposition
 * exactly as a failed discard left it before the mutation boundary existed.
 * Only by compare: the row must still be that decision (`discard-pending`,
 * not consumed, and no registry destination state other than `unresolved`);
 * a registry answer merged meanwhile keeps the decision. Never throws: a
 * failed withdrawal leaves `discard-pending`, which the queue pass completes.
 * Returns the restored row, or null when nothing was restored, so the caller
 * can publish it to this page's mirror (FT10, 2026-10-19: a second Discard tap
 * on the same page had published `discard-pending`, and the offer then stayed
 * hidden after this withdrawal until a reload). `verifiedCopy` (KR8): the
 * discard's verified copy of the bytes, stored in place of the Blob read here.
 */
async function withdrawDiscardDecision(
  id: string,
  ownerId: string,
  previous: db.AudioQueueRow['localDisposition'],
  verifiedCopy: db.VerifiedCaptureCopy | null,
): Promise<db.AudioQueueRow | null> {
  try {
    return await db.updateAudioQueueRowIfPresent(id, ownerId, (current) => {
      if (
        current.localDisposition !== 'discard-pending' ||
        rowConsumedBySave(current) ||
        (current.destinationState !== undefined && current.destinationState !== 'unresolved')
      ) return null;
      const { localDisposition: _decided, ...rest } = current;
      void _decided;
      return previous === undefined ? rest : { ...rest, localDisposition: previous };
    }, verifiedCopy);
  } catch {
    // The decision stands; the queue pass completes the discard.
    return null;
  }
}

/**
 * Reactive lookup of an item's transcribed text. Returns null when
 * the item is still pending. Caller uses this to gate the resume
 * card's "Continue recording" button (R8 waiting state).
 */
export async function getQueueItemText(id: string): Promise<string | null> {
  const meta = useAudioQueueStore.getState().items.find((i) => i.id === id);
  return meta?.transcribedText ?? null;
}

export interface InlineAudioDraftRecovery {
  queueItemId: string;
  /** Words to merge into the draft (a recording this draft can still claim). */
  transcribedText: string | null;
  /**
   * DS fix round 1 (REVIEW-DS1 F1): the recording's own words, for the
   * held-recording check at Save only (findHeldRecordingConflicts); never
   * merged into the draft. Kept so a row deleted after this read still
   * produces the conflict.
   */
  heldWords?: string | null;
  /**
   * DS fix round 1 (F2): the row is not on this device. The id stays held with
   * its words unknown (the caller decides what it treats as those words); a
   * missing row is never proof that anything was saved.
   */
  rowMissing?: true;
}

/**
 * DS (duplicate-save follow-through, 2026-10-20): a prayer recording in the
 * resume card's lane (a new-prayer recording linked to no prayer, not awaiting
 * an inline save, unresolved and not consumed by a Save) whose words the card's
 * Continue moved into the Home draft. The draft's Save claims it as it claims a
 * recording awaiting an inline save (`inline-prayer`), so the recording is bound
 * to the prayer its words became and is not offered again. The source must be
 * exactly 'prayer', as the commit transaction requires
 * (content-lifecycle/store.ts checks the same shape again).
 */
function heldResumeCardRecording(row: db.AudioQueueRow): boolean {
  return row.awaitingInlineSave !== true &&
    row.prayerId == null &&
    row.recoveryKind === 'new-prayer' &&
    row.inlineSaveSource === 'prayer' &&
    row.localDisposition === 'unresolved' &&
    (row.destinationState === undefined || row.destinationState === 'unresolved') &&
    row.destinationOpId == null;
}

/**
 * Reconnect one persisted draft queue identity to its owner-bound row after a
 * reload. Returning null lets the caller prune a stale companion ID. While
 * voice is off, a byte-bearing row is still returned (so its durable draft id
 * stays indexed) but its transcript is withheld to prevent duplicate adoption.
 *
 * DS (2026-10-20): a prayer recording that another page already linked to a
 * prayer or that a Save consumed is also returned, with no transcript, so the
 * draft keeps knowing it holds that recording's words and its Save can refuse
 * to save them twice (findHeldRecordingConflicts); its words are never merged
 * into the draft again. A resume-card recording whose words Continue moved into
 * the draft is returned like an inline-save recording. Another owner's row or
 * another source is still pruned.
 *
 * Fix round 1 (REVIEW-DS1, 2026-10-20): every returned recording also carries
 * its own words as `heldWords` (F1), and a prayer draft's recording whose row
 * is missing is returned as `rowMissing` instead of being pruned (F2), so a
 * recording saved elsewhere and then deleted after its receipt is still
 * recognised at Save after a reopen.
 */
export async function getInlineAudioDraftRecovery(
  queueItemId: string,
  source: 'prayer' | 'bookmark',
): Promise<InlineAudioDraftRecovery | null> {
  const ownerId = await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) return null;
  const row = await db.getAudioQueueRow(queueItemId);
  if (!row) {
    return source === 'prayer' ? { queueItemId, transcribedText: null, heldWords: null, rowMissing: true } : null;
  }
  if (
    row.ownerId !== ownerId ||
    normalizeSource(row.inlineSaveSource) !== source
  ) return null;
  const words = row.transcribedText?.trim() || null;
  const prayerRecording = source === 'prayer' && normalizeRecoveryKind(row.recoveryKind) === 'new-prayer';
  if (prayerRecording && (row.prayerId != null || rowConsumedBySave(row))) {
    return { queueItemId, transcribedText: null, heldWords: words };
  }
  if (!row.awaitingInlineSave && !(prayerRecording && heldResumeCardRecording(row))) return null;
  return {
    queueItemId,
    transcribedText: audioQueueDeletionAllowed(row, enhancedVoiceAllowed()) ? words : null,
    heldWords: words,
  };
}

/** DS (2026-10-20): what findHeldRecordingConflicts found, by queue id. */
export interface HeldRecordingConflicts {
  saved: string[];
  unknown: string[];
}

/**
 * DS (2026-10-20): recordings an editor holds (their words reached it: Home's
 * own recordings, the resume card's Continue, the Pray on Scripture sheet's
 * recordings) that are no longer this editor's to claim because they left its
 * hands elsewhere. Read before the editor's typed Save, so that Save never
 * silently turns one recording into two prayers.
 * - `saved`: the same owned recording is already linked to an existing Prayer,
 *   or bound into one by another Save, proven from durable state by
 *   db.readPendingPrayerLinkProof (the proof a stale duplicate Save uses);
 * - `unknown`: linked or consumed without that proof, or the row is no longer
 *   on this device. A missing row is not proof that anything was saved, so it
 *   is never reported as saved.
 * A recording linked to no prayer and not consumed (claimable, waiting for its
 * own recovery, or with a recorded discard) is not a conflict: the Save's claim
 * or the recording's own recovery decides it. Only recordings whose words the
 * text still carries count, by the containment test the inline claim itself
 * uses (`held` gives the words this editor received, used when the row is
 * gone). The decision is by the recording's identity and stored state; no
 * prayer is ever matched by its words. Reads only; throws when no owner is
 * signed in, so the caller fails closed.
 */
export async function findHeldRecordingConflicts(
  held: ReadonlyMap<string, string>,
  committedText: string,
  expectedAnchor: ScriptureAnchor | null,
): Promise<HeldRecordingConflicts> {
  const conflicts: HeldRecordingConflicts = { saved: [], unknown: [] };
  const text = committedText.trim();
  if (!text || held.size === 0) return conflicts;
  const ownerId = await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) {
    throw new Error('held-recording-owner-unavailable');
  }
  for (const [queueItemId, words] of held) {
    const row = await db.getAudioQueueRow(queueItemId);
    if (!row) {
      if (containsRecoveredVoiceText(text, words)) conflicts.unknown.push(queueItemId);
      continue;
    }
    if (row.ownerId !== ownerId || normalizeSource(row.inlineSaveSource) !== 'prayer') continue;
    const recovered = row.transcribedText?.trim() || words.trim();
    if (!recovered || !containsRecoveredVoiceText(text, recovered)) continue;
    if (row.prayerId == null && !rowConsumedBySave(row)) continue;
    let proof: db.PendingPrayerLinkProof | null = null;
    try {
      proof = await db.readPendingPrayerLinkProof(queueItemId, ownerId, expectedAnchor ?? undefined);
    } catch {
      // A failed read proves nothing.
    }
    (proof?.saved ? conflicts.saved : conflicts.unknown).push(queueItemId);
  }
  return conflicts;
}

/**
 * Build fail-closed claims for an upcoming Save transaction. These reads are
 * only a preflight; commitAuthoredMutation repeats every predicate and binds
 * all accepted queue ids atomically with the content operation.
 *
 * A row this Save can no longer claim also refreshes this page's mirror, in
 * memory only: a recorded discard (`discard-pending`, perhaps another page's)
 * or another Save's consumption is published, and a row that no longer exists
 * leaves the mirror, so a stale offer does not return once the box clears.
 */
export async function collectInlineAudioCommitClaims(
  queueItemIds: Iterable<string>,
  source: 'prayer' | 'bookmark',
  committedText: string,
): Promise<RecoveredAudioCommitClaim[]> {
  const listFence = captureQueueListFence();
  const ownerId = await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) return [];
  const normalizedText = committedText.trim();
  if (!normalizedText) return [];
  const claims: RecoveredAudioCommitClaim[] = [];
  for (const queueItemId of new Set(queueItemIds)) {
    const row = await db.getAudioQueueRow(queueItemId);
    if (!row) {
      await forgetMissingRow(queueItemId, ownerId, listFence);
      continue;
    }
    publishUnclaimableRow(row, ownerId, listFence);
    const recovered = row?.transcribedText?.trim() ?? '';
    if (
      !row ||
      row.ownerId !== ownerId ||
      row.audioProtocolVersion !== 1 ||
      row.localDisposition !== 'unresolved' ||
      // DS: also a resume-card recording whose words Continue moved into the draft.
      !(row.awaitingInlineSave || (source === 'prayer' && heldResumeCardRecording(row))) ||
      normalizeSource(row.inlineSaveSource) !== source ||
      row.recoveryKind !== (source === 'prayer' ? 'new-prayer' : 'bookmark') ||
      !recovered ||
      !containsRecoveredVoiceText(normalizedText, recovered) ||
      !audioQueueDeletionAllowed(row, enhancedVoiceAllowed())
    ) continue;
    claims.push({
      queueItemId,
      recoveryKind: source === 'prayer' ? 'inline-prayer' : 'inline-bookmark',
      expectedTranscribedText: recovered,
    });
  }
  return claims;
}

/**
 * Thrown by `createPendingPrayerFromAudio` in place of
 * `pending-prayer-audio-already-linked` only when the durable proof holds
 * (`db.readPendingPrayerLinkProof`): the same owned recording already has its
 * valid destination, so the caller may show its saved outcome. Every other
 * refusal keeps its original message and the surface's failure copy.
 */
export const PENDING_PRAYER_ALREADY_SAVED = 'pending-prayer-audio-already-saved';

/**
 * Persist a pending Prayer and its queue destination as one owner-checked
 * transaction, then update both in-memory mirrors after commit.
 */
export async function createPendingPrayerFromAudio(
  prayer: Prayer,
  queueItemId: string,
): Promise<void> {
  assertCoreWriteAllowed('audio:create-pending-prayer');
  const listFence = captureQueueListFence();
  const expectedOwnerId = await getCurrentUserId();
  if (!expectedOwnerId || !(await ownerGateStillOpen(expectedOwnerId))) {
    throw new Error('createPendingPrayerFromAudio: owner changed');
  }
  // KR8 (db.ts "Verified capture copies"): linking rewrites the recording's
  // row, so it stores a verified copy of the bytes, read and proved now, before
  // the transaction; the processor's upload then reads a recording that reads.
  // Bytes whose read fails are not rewritten: the Save is refused before the
  // link transaction (LC2, db.ts `refuseUnreadableRecordingWrite`), nothing is
  // written and the Save reports its failure. Bytes that read but cannot be
  // verified get no copy: the link keeps the stored Blob, as before.
  const materialized = await db.materializeVerifiedCaptureCopy(queueItemId, expectedOwnerId);
  db.refuseUnreadableRecordingWrite(materialized);
  let committed: Awaited<ReturnType<typeof db.savePendingPrayerAndLinkAudio>>;
  try {
    committed = await db.savePendingPrayerAndLinkAudio(
      prayer,
      queueItemId,
      expectedOwnerId,
      materialized.status === 'verified' ? materialized.copy : null,
    );
  } catch (error) {
    // A stale page's duplicate Save: another page already linked this exact
    // row. The Save is reported as already done ONLY after the durable proof
    // (db.readPendingPrayerLinkProof: this owner's binding, this owner's row
    // naming an existing Prayer that still names it as pending or has bound
    // it, and the passage provenance this Save would have linked). Only then
    // is this page's mirror refreshed from the durable row (no write; its
    // disposition and destination included), so the stale offer leaves here
    // too, and the distinct message is thrown. An
    // unproven already-linked row keeps the original refusal AND the mirror
    // as it was: the surface's failure copy stays honest, and the resume card
    // (which lists only rows naming no Prayer) stays mounted to show it.
    if (error instanceof Error && error.message === 'pending-prayer-audio-already-linked') {
      let proven: db.PendingPrayerLinkProof | null = null;
      try {
        proven = await db.readPendingPrayerLinkProof(
          queueItemId,
          expectedOwnerId,
          prayer.scriptureAnchor,
        );
      } catch {
        // A failed read proves nothing; the original refusal is what the caller learns.
      }
      if (proven?.saved) {
        // The durable row's disposition and destination come along, as
        // dequeueCommittedInlineAudio publishes them: a row another page's
        // typed Save consumed through its inline-prayer claim keeps
        // `awaitingInlineSave` but is `destination-commit-pending`, which is
        // what retires this page's inline-save offer.
        queueList().updateItem(listFence, queueItemId, {
          prayerId: proven.row.prayerId,
          awaitingInlineSave: proven.row.awaitingInlineSave,
          attemptCount: proven.row.attemptCount,
          failed: proven.row.failed,
          localDisposition: proven.row.localDisposition,
          destinationEntityType: proven.row.destinationEntityType,
          destinationEntityId: proven.row.destinationEntityId,
          destinationOpId: proven.row.destinationOpId,
        });
        throw new Error(PENDING_PRAYER_ALREADY_SAVED);
      }
    }
    // Nothing was saved, so the refusal and its failure copy stand. A
    // recording that no longer exists, or whose discard is recorded, also
    // leaves this page's mirror (in memory only) so its stale offer leaves.
    if (error instanceof Error && error.message === 'pending-prayer-audio-missing') {
      await forgetMissingRow(queueItemId, expectedOwnerId, listFence);
    } else if (error instanceof Error && error.message === 'pending-prayer-audio-discard-pending') {
      try {
        const current = await db.getAudioQueueRow(queueItemId);
        if (current && (await ownerGateStillOpen(expectedOwnerId))) {
          publishUnclaimableRow(current, expectedOwnerId, listFence);
        }
      } catch {
        // A failed read leaves the mirror as it was; the refusal stands.
      }
    }
    throw error;
  }
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  useAdiaStore.setState((state) => ({
    prayers: [
      committed.prayer,
      ...state.prayers.filter((item) => item.id !== committed.prayer.id),
    ],
  }));
  queueList().updateItem(listFence, queueItemId, {
    prayerId: committed.row.prayerId,
    awaitingInlineSave: committed.row.awaitingInlineSave,
    attemptCount: committed.row.attemptCount,
    failed: committed.row.failed,
  });
  processQueueInBackground();
}

/**
 * Explicit user-discard helper. Capture start and successful transcription
 * never call this path because unresolved audio is recoverable content, not a
 * replaceable cache.
 *
 * Source-scoped (bookmarks Phase 2): clears ONLY orphans whose
 * normalized source matches `source`, so a prayer recording can never
 * wipe a pending bookmark orphan and vice versa. 'unknown'-normalized
 * rows match neither 'prayer' nor 'bookmark' and are left untouched.
 *
 * A row a Save already consumed (its claim kept `awaitingInlineSave` but bound
 * the row) is skipped and published to the mirror, so a stale offer leaves;
 * `dequeueAudio` repeats the selection at its mutation boundary. A mirror item
 * whose row no longer exists leaves the mirror (`forgetMissingInlineSaveItems`).
 */
export async function clearAwaitingInlineSaveOrphans(
  source: 'prayer' | 'bookmark',
  expectedOwnerId?: string,
): Promise<string[]> {
  if (isCoreReadOnly()) return [];
  const listFence = captureQueueListFence();
  try {
    const ownerId = expectedOwnerId ?? await getCurrentUserId();
    if (!ownerId || !(await ownerGateStillOpen(ownerId))) return [];
    const rows = await db.getAllAudioQueueRows();
    await forgetMissingInlineSaveItems(rows, source, ownerId, listFence);
    const voiceEnabled = enhancedVoiceAllowed();
    const removed: string[] = [];
    for (const row of rows) {
      if (!awaitingInlineSaveFrom(row, source)) continue;
      if (rowConsumedBySave(row)) {
        publishCommittedRow(row, ownerId, listFence);
        continue;
      }
      if (audioQueueDeletionAllowed(row, voiceEnabled)) {
        const discarded = await dequeueAudio(
          row.id,
          ownerId,
          listFence,
          (current) => awaitingInlineSaveFrom(current, source),
        );
        if (discarded) removed.push(row.id);
      }
    }
    return removed;
  } catch {
    console.error('[Adia] clearAwaitingInlineSaveOrphans failed');
    return [];
  }
}

/**
 * Home's rescue Discard (useVoiceCapture.clearPendingInlineSave('prayer')):
 * discard exactly the recording this page's offer stands for, its mirror head
 * chosen as the Save claims it (`homeInlineSaveOffer` since RU item 2,
 * 2026-10-20: it can be a recording with a valid Bible passage), through
 * `discardAwaitingInlineSaveAudio`. When that recording is no longer
 * offered (a Save consumed or linked it, its discard is recorded elsewhere, or
 * it is gone) only this page's mirror learns it; no other recording is chosen
 * in its place (B1, 2026-10-19: a durable "newest still awaiting" choice could
 * delete an older, unrelated recording or a newer Pray-on-Scripture one).
 */
export async function discardLatestAwaitingInlineSaveOrphan(
  source: 'prayer' | 'bookmark',
  expectedOwnerId?: string,
): Promise<string[]> {
  if (isCoreReadOnly()) return [];
  const listFence = captureQueueListFence();
  const items = queueList().items;
  const head = source === 'prayer' ? homeInlineSaveOffer(items)?.item ?? null : newestInlineSaveOffer(items, source, null);
  if (!head) return [];
  const discarded = await discardOfferedInlineSave(head.id, source, expectedOwnerId, listFence);
  return discarded ? [head.id] : [];
}

/**
 * Explicitly discard one awaiting-inline-save row from the named surface.
 * While Enhanced Voice is disabled this remains available only for truly
 * text-only rows; byte-bearing rows stay parked even after a user gesture.
 * The exact id + source check prevents a bookmark surface from deleting
 * prayer recovery (and vice versa). A row that is no longer the offered
 * recording (consumed or linked by a Save, its discard already recorded, or
 * another surface's) is refused with its state published to the mirror, so a
 * stale offer leaves; a row that no longer exists removes its mirror item.
 */
export async function discardAwaitingInlineSaveAudio(
  queueItemId: string,
  source: 'prayer' | 'bookmark',
  expectedOwnerId?: string,
): Promise<boolean> {
  return discardOfferedInlineSave(queueItemId, source, expectedOwnerId, captureQueueListFence());
}

async function discardOfferedInlineSave(
  queueItemId: string,
  source: 'prayer' | 'bookmark',
  expectedOwnerId: string | undefined,
  listFence: QueueFence,
): Promise<boolean> {
  if (isCoreReadOnly()) return false;
  const ownerId = expectedOwnerId ?? await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) return false;
  const row = await db.getAudioQueueRow(queueItemId);
  if (!row) {
    await forgetMissingRow(queueItemId, ownerId, listFence);
    return false;
  }
  if (!inlineSaveRowStillOffered(row, source)) {
    publishRowState(row, ownerId, listFence);
    return false;
  }
  if (!audioQueueDeletionAllowed(row, enhancedVoiceAllowed())) return false;
  return dequeueAudio(
    row.id,
    ownerId,
    listFence,
    (current) => inlineSaveRowStillOffered(current, source),
  );
}

/**
 * The resume card's Discard (useVoiceCapture.dismissInterruption), also used
 * after Continue or Keep typing moved the words into the draft: removes the
 * interrupted recording only while its row is still what the card offered
 * (`interruptedRecordingStillOffered`), checked at `dequeueAudio`'s mutation
 * boundary. A stale card on another page after "Save what I have" linked the
 * row to a pending Prayer is refused with no write and no registry request,
 * and the row's state is published so that card leaves; the pending Prayer
 * keeps its only recording.
 */
export async function discardInterruptedRecording(
  queueItemId: string,
  expectedOwnerId?: string,
): Promise<boolean> {
  return dequeueAudio(
    queueItemId,
    expectedOwnerId,
    captureQueueListFence(),
    interruptedRecordingStillOffered,
  );
}

/**
 * After a Save that consumed inline-save recordings through its own claims,
 * publish each consumed row's durable state to this page's queue mirror, so
 * the page's own inline-save offer (and its Discard) leaves at once instead of
 * after the content receipt. Reads IndexedDB only and writes the in-memory
 * mirror only: the durable row is unchanged and still releases after its
 * receipt (finalizeCommittedAudio). Only rows of this owner that the commit
 * bound (`destination-commit-pending`) are published. Never rejects: callers
 * fire it without awaiting, and a failed session or owner-gate read only
 * leaves the mirror as it was.
 */
export async function publishCommittedInlineAudio(
  queueItemIds: readonly string[],
  expectedOwnerId?: string,
): Promise<void> {
  if (queueItemIds.length === 0) return;
  const listFence = captureQueueListFence();
  let ownerId: string | null | undefined;
  try {
    ownerId = expectedOwnerId ?? await getCurrentUserId();
    if (!ownerId || !(await ownerGateStillOpen(ownerId))) return;
  } catch {
    return;
  }
  for (const queueItemId of queueItemIds) {
    let row: db.AudioQueueRow | undefined;
    try {
      row = await db.getAudioQueueRow(queueItemId);
    } catch {
      continue;
    }
    if (row) publishCommittedRow(row, ownerId, listFence);
  }
}

/**
 * After a typed Save failed (for instance its commit refused a claim whose row
 * another page's discard had already taken), bring this page's mirror up to
 * the durable state of the recordings that Save tried to claim, in memory only:
 * a row no Save may claim (`discard-pending`, or consumed by another Save) is
 * published, and a row that no longer exists leaves the mirror. Writes no
 * IndexedDB and changes no failure copy. Never rejects: callers fire it without
 * awaiting.
 */
export async function refreshInlineAudioMirror(
  queueItemIds: readonly string[],
  expectedOwnerId?: string,
): Promise<void> {
  if (queueItemIds.length === 0) return;
  const listFence = captureQueueListFence();
  let ownerId: string | null | undefined;
  try {
    ownerId = expectedOwnerId ?? await getCurrentUserId();
    if (!ownerId || !(await ownerGateStillOpen(ownerId))) return;
  } catch {
    return;
  }
  for (const queueItemId of queueItemIds) {
    let row: db.AudioQueueRow | undefined;
    try {
      row = await db.getAudioQueueRow(queueItemId);
    } catch {
      continue;
    }
    if (row) publishUnclaimableRow(row, ownerId, listFence);
    else await forgetMissingRow(queueItemId, ownerId, listFence);
  }
}

/**
 * Remove only inline audio whose durable transcript is present in a
 * successfully committed destination. Failed/untranscribed rows never match
 * and remain available for explicit recovery or encrypted export.
 */
export async function dequeueCommittedInlineAudio(
  queueItemId: string,
  source: 'prayer' | 'bookmark',
  committedText: string,
  destinationEntityId: string,
  expectedOwnerId?: string,
): Promise<boolean> {
  if (isCoreReadOnly()) return false;
  const listFence = captureQueueListFence();
  const ownerId = expectedOwnerId ?? await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) return false;
  const context = await getCurrentContentLifecycleContext();
  if (context.ownerId !== ownerId) return false;
  const row = await attachInlineAudioToPendingOperation(
    context,
    queueItemId,
    source,
    committedText,
    source === 'bookmark' ? 'bookmark' : 'prayer',
    destinationEntityId,
  );
  if (!row) return false;
  queueList().updateItem(listFence, row.id, {
    awaitingInlineSave: false,
    prayerId: row.prayerId,
    localDisposition: row.localDisposition,
    destinationEntityType: row.destinationEntityType,
    destinationEntityId: row.destinationEntityId,
    destinationOpId: row.destinationOpId,
  });
  try {
    await syncContentLifecycle();
    return finalizeCommittedAudio(row.id, ownerId, listFence);
  } catch {
    // The exact op binding is durable locally. Reconnect/processQueue retries
    // bind -> content receipt -> registry cleanup without duplicating commit.
    return true;
  }
}

/**
 * Bind/reconcile one local commit-pending row. This is idempotent and is also
 * called from the queue pass after reload or a lost HTTP response.
 */
export async function finalizeCommittedAudio(
  queueItemId: string,
  expectedOwnerId?: string,
  listFence: QueueFence = captureQueueListFence(),
): Promise<boolean> {
  const ownerId = expectedOwnerId ?? await getCurrentUserId();
  if (!ownerId || !(await ownerGateStillOpen(ownerId))) return false;
  const row = await db.getAudioQueueRow(queueItemId);
  if (!row || row.ownerId !== ownerId) return false;
  if (row.captureMode === 'browser' && db.audioQueueRowIsTextOnly(row)) {
    if (
      !row.destinationOpId ||
      !row.destinationEntityType ||
      !row.destinationEntityId ||
      !await hasAuthoritativeContentReceipt({
        ownerId,
        opId: row.destinationOpId,
        entityType: row.destinationEntityType,
        entityId: row.destinationEntityId,
      })
    ) return false;
    const deleted = await db.deleteAudioQueueRow(
      row.id,
      ownerId,
      localDeletionAuthority(row, 'content-receipt'),
    );
    if (deleted) queueList().removeItem(listFence, row.id);
    return deleted;
  }
  if (
    !row.destinationOpId ||
    !row.destinationEntityType ||
    !row.destinationEntityId ||
    !await hasAuthoritativeContentReceipt({
      ownerId,
      opId: row.destinationOpId,
      entityType: row.destinationEntityType,
      entityId: row.destinationEntityId,
    })
  ) return false;
  let reconciled: Awaited<ReturnType<typeof reconcileRegistryAudio>>;
  try {
    // Reconcile first: bound/terminal rows replay directly, including the
    // immutable deletion receipt after cron cleanup. Only an unresolved row
    // needs the one-time bind transition below.
    reconciled = await reconcileRegistryAudio(row, listFence);
  } catch (error) {
    if (!(error instanceof AudioRegistryProtocolError) || error.code !== 'state_conflict') {
      throw error;
    }
    const refreshed = await db.getAudioQueueRow(row.id);
    if (!refreshed || refreshed.ownerId !== ownerId) return false;
    const bound = await bindRegistryToContentReceipt(refreshed, listFence);
    if (!bound) return false;
    reconciled = await reconcileRegistryAudio(bound, listFence);
  }
  if (!reconciled?.authority) return false;
  const deleted = await db.deleteAudioQueueRow(
    reconciled.row.id,
    ownerId,
    reconciled.authority,
  );
  if (deleted) queueList().removeItem(listFence, row.id);
  return deleted;
}

/**
 * Retired compatibility entry point retained for cutover probes. Runtime boot
 * no longer calls it: a bookmark recording can contain the user's only copy,
 * so cleanup requires an explicit destination commit or discard.
 */
export async function purgeBookmarkInlineSaveOrphans(): Promise<void> {
  // Explicit, as for every queue entry point (probe-core-readonly): read-only
  // mode is a no-op here too; the function is a no-op in every mode.
  if (isCoreReadOnly()) return;
}

/**
 * Drive the queue. Single-flight: if a run is in flight, returning
 * callers no-op rather than racing. Triggered by:
 *   - enqueueAudio (immediately after a fresh entry is persisted)
 *   - main.tsx `online` event (network came back)
 *   - main.tsx `visibilitychange → visible` (user returned to tab)
 *   - hydrate completion (catch up after reload)
 */
export async function processQueue(): Promise<void> {
  await queueDrain.request();
}

/**
 * `processQueue` for a background trigger (legacy compatibility contract,
 * section 10): a pass that stopped (an owner change, a storage failure) is
 * logged and left stopped; it is never continued or retried from here, and it
 * never becomes an unhandled rejection.
 */
export function processQueueInBackground(): void {
  processQueue().catch((error: unknown) => {
    console.error('[Adia] queue pass stopped', error instanceof Error ? error.name : typeof error);
  });
}

/**
 * The list fence of the queue pass in progress. Passes never overlap (the
 * single-flight drain runs one at a time), so the row work a pass starts takes
 * its fence from here, captured at the pass's entry.
 */
let queuePassFence: QueueFence = null;

async function runQueuePass(): Promise<void> {
  if (isCoreReadOnly()) return;
  // One pass is one operation: every list update it makes uses this fence.
  const listFence = captureQueueListFence();
  queuePassFence = listFence;

  const expectedOwnerId = await getCurrentUserId();
  if (!expectedOwnerId || !(await ownerGateStillOpen(expectedOwnerId))) return;

  // Snapshot from IDB so we always work against the persistent truth
  // (the in-memory mirror could be hydrated late). Oldest first.
  const rows = await db.getAllAudioQueueRows();
  for (const row of rows) {
    if (!(await ownerGateStillOpen(expectedOwnerId))) break;
    if (row.localDisposition === 'destination-commit-pending') {
      await finalizeCommittedAudio(row.id, expectedOwnerId, listFence).catch(() => false);
      continue;
    }
    if (row.localDisposition === 'discard-pending') {
      await dequeueAudio(
        row.id,
        expectedOwnerId,
        listFence,
        (current) => current.localDisposition === 'discard-pending',
      ).catch(() => false);
      continue;
    }
    if (
      row.localDisposition === 'quarantined' ||
      row.ownerId !== expectedOwnerId ||
      row.audioProtocolVersion !== 1 ||
      normalizeRecoveryKind(row.recoveryKind) === 'unknown'
    ) continue;
    if (row.failed) continue;
    // Rows awaiting an explicit new-prayer Save stay parked until claimed.
    if (row.awaitingInlineSave) continue;
    if (inFlightItems.has(row.id)) continue;
    await processRowWithCrossTabLock(row.id, expectedOwnerId);
  }
}

async function processRowWithCrossTabLock(
  rowId: string,
  expectedOwnerId: string,
): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.locks) return;
  await navigator.locks.request(
    `adia-audio:${expectedOwnerId}:${rowId}`,
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock || inFlightItems.has(rowId)) return;
      markItemInFlight(rowId);
      try {
        const fresh = await db.getAudioQueueRow(rowId);
        if (!fresh || fresh.failed || fresh.awaitingInlineSave) return;
        if (fresh.transcribedText) {
          // Browser-text recovery has no audio bytes and can route locally
          // while cloud voice is closed. A text-ready enhanced row still
          // indexes a Blob/Storage object; consuming it would orphan those
          // preserved bytes, so it stays parked until Enhanced Voice returns.
          if (!audioQueueDeletionAllowed(fresh, enhancedVoiceAllowed())) return;
          // Text-ready rows require no network. Route them even while the
          // browser is offline so a recovered browser transcript can reach
          // its already-local destination immediately.
          await routeTranscribedText(fresh, expectedOwnerId);
        } else {
          // Only upload/transcription work is network-bound. Keeping this
          // check below the text-ready branch avoids parking local recovery
          // behind navigator.onLine.
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return;
          }
          await processOne(fresh, expectedOwnerId);
        }
      } finally {
        clearItemInFlight(rowId);
      }
    },
  );
}

const queueDrain = createSingleFlightDrain(runQueuePass);

/**
 * RU fix round 1 (2026-10-20): whether this page cannot read the stored bytes
 * of `queueItemId`. Every write that carries those bytes is then refused for
 * the same reason (db.refuseUnreadableRecordingWrite): "Save what I have", a
 * Discard (dequeueAudio) and the close after Continue, until the app is
 * reopened. Reads only: the same verified-copy read those writes make before
 * their transactions, and the same test; nothing is written or kept. False when
 * the row is missing, holds no Blob, reads, or there is no signed-in owner.
 */
export async function recordingUnreadableHere(queueItemId: string): Promise<boolean> {
  const ownerId = await getCurrentUserId();
  if (!ownerId) return false;
  const result = await db.materializeVerifiedCaptureCopy(queueItemId, ownerId);
  return result.status === 'unverified' && result.reason === 'read-failed';
}

/** A refusal for unreadable recording bytes (db.ts `refuseUnreadableRecordingWrite`): nothing was written. */
function recordingBytesUnreadable(error: unknown): boolean {
  return error instanceof db.CaptureBytesUnverifiedError && error.reason === 'read-failed';
}

/** A refusal for bytes that do not match their reference (db.ts `isRecordingBytesMismatch`): nothing was sent or written. */
function recordingBytesMismatched(error: unknown): boolean {
  return error instanceof db.CaptureBytesUnverifiedError && db.isRecordingBytesMismatch(error.reason);
}

/**
 * The queue run leaves this recording as it is (nothing written, no attempt
 * counted) and goes on. The owner gate is checked again after the awaits that
 * led here; only then is the reason recorded for this page (the fenced list
 * update also requires the pass's own scope and the current auth), so the
 * person sees why instead of a finishing indicator (LC4). The console line is
 * diagnostic only.
 */
async function noteNotProcessed(
  rowId: string,
  expectedOwnerId: string,
  listFence: QueueFence,
  attention: RecordingAttention,
): Promise<void> {
  console.error(attention === 'mismatch'
    ? '[Adia] queue row not processed: recording bytes do not match their reference'
    : '[Adia] queue row not processed: recording bytes unreadable');
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  queueList().setAttention(listFence, rowId, attention);
}

async function processOne(
  row: db.AudioQueueRow,
  expectedOwnerId: string,
  listFence: QueueFence = queuePassFence,
): Promise<void> {
  // Browser-native speech can leave an intentional text-only queue row, which
  // remains routable while cloud audio is disabled. Rows that still carry a
  // Blob/Storage pointer park too, even if their transcript is already ready;
  // the routing branch above enforces that before processOne is entered.
  if (!enhancedVoiceAllowed()) return;
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  // A canonical path is an address, never proof that Storage committed the
  // bytes. Only a persisted non-`registered` registry receipt allows this
  // worker to drop the durable Blob or advance to transcription.
  if (!row.registryState || !UPLOAD_PROVED_REGISTRY_STATES.has(row.registryState)) {
    if (!row.blob) {
      console.error('[Adia] processOne: upload is unproved and Blob is unavailable');
      await markRowTerminallyFailed(row, expectedOwnerId, listFence);
      return;
    }
    const userId = await getCurrentUserId();
    if (userId !== expectedOwnerId) return;
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    // KR8 (A2): the recording's bytes are proved once for this attempt, before
    // the upload; the same copy is uploaded and, if the attempt fails, stored by
    // the attempt write, which therefore reads no bytes again (a second read
    // could race a page being left, which WebKit reports as a page error).
    const attempt = await db.materializeVerifiedCaptureCopy(row.id, expectedOwnerId);
    // Queue progress (legacy compatibility contract, section 10): when this
    // attempt's own read of the recording's bytes fails, nothing is written for
    // it in this pass (no attempt counted, no terminal state, no request) and
    // the pass goes on to the next row. Only that byte read decides: a failure
    // to read the row itself, like every other storage failure, still stops
    // the pass. A recording made before v0.2.0 without a kept proof is read
    // here for that purpose only.
    if (
      attempt.status === 'unverified'
        ? attempt.reason === 'read-failed'
        : attempt.status === 'not-applicable' && !(await db.storedRecordingBytesReadable(row.id))
    ) {
      await noteNotProcessed(row.id, expectedOwnerId, listFence, 'unreadable');
      return;
    }
    // LC4: bytes that read but do not match their reference are never sent and
    // nothing is written for them (an attempt write would, in WebKit, leave them
    // unreadable in this page); the prayer says so instead of finishing.
    if (attempt.status === 'unverified' && db.isRecordingBytesMismatch(attempt.reason)) {
      await noteNotProcessed(row.id, expectedOwnerId, listFence, 'mismatch');
      return;
    }
    // Past both gates: whatever this page said about the recording no longer
    // holds (the list update is fenced like every other).
    queueList().setAttention(listFence, row.id, null);
    const attemptCopy = attempt.status === 'verified' ? attempt.copy : null;
    try {
      const ext = extFromMimeType(row.mimeType);
      const newPath = await uploadAudioToStorage(row.blob, row.id, userId, ext, listFence, attemptCopy ?? undefined);
      if (!(await ownerGateStillOpen(expectedOwnerId))) return;
      // uploadAudioToStorage persisted the provider-observed receipt first.
      // Only now may this atomic write release the local Blob.
      const persisted = await db.updateAudioQueueRowIfPresent(
        row.id,
        expectedOwnerId,
        (current) =>
          current.registryState && UPLOAD_PROVED_REGISTRY_STATES.has(current.registryState)
            ? { ...current, storagePath: newPath, blob: undefined }
            : null,
        // Releases the Blob: nothing to copy (KR8).
        null,
      );
      if (!persisted) return;
      if (!(await ownerGateStillOpen(expectedOwnerId))) return;
      queueList().updateItem(listFence, row.id, {
        storagePath: newPath,
      });
      row = persisted;
    } catch (error) {
      // A write or read in this attempt found the recording's bytes unreadable
      // and was refused before anything was written (LC2, decision 1): as for
      // the byte read above, no attempt is counted and the pass goes on.
      if (recordingBytesUnreadable(error)) {
        await noteNotProcessed(row.id, expectedOwnerId, listFence, 'unreadable');
        return;
      }
      if (recordingBytesMismatched(error)) {
        await noteNotProcessed(row.id, expectedOwnerId, listFence, 'mismatch');
        return;
      }
      console.error('[Adia] processOne: storage migration upload failed');
      if (await ownerGateStillOpen(expectedOwnerId)) {
        // When this attempt has no verified copy, its attempt write would carry
        // the stored Blob, so this row's own bytes are read first (LC3, the
        // operator's narrow isolation): the attempt's own rewrite can leave them
        // unreadable in this page (WebKit), and a write carrying them could then
        // fail and stop the run. When they no longer read, nothing is written
        // for this row (no attempt counted), it is reported not processed, and
        // the run goes on. Only that byte read decides: a failure to read the
        // row itself throws and stops the run, as before. An attempt with a
        // verified copy stores that copy and reads nothing again (a second read
        // can race a page being left, which WebKit reports as a page error).
        if (!attemptCopy && !(await db.storedRecordingBytesReadable(row.id))) {
          await noteNotProcessed(row.id, expectedOwnerId, listFence, 'unreadable');
          return;
        }
        // A recording made before v0.2.0 had no proof when this attempt began;
        // the upload's registration may have kept one, so the attempt write
        // proves its own copy (undefined) instead of being told there is none.
        try {
          await bumpAttempt(row, expectedOwnerId, listFence, attempt.status === 'not-applicable' ? undefined : attemptCopy);
        } catch (bumpError) {
          if (!recordingBytesUnreadable(bumpError)) throw bumpError;
          await noteNotProcessed(row.id, expectedOwnerId, listFence, 'unreadable');
        }
      }
      return;
    }
  } else if (!row.storagePath) {
    console.error('[Adia] processOne: upload receipt has no canonical path');
    await markRowTerminallyFailed(row, expectedOwnerId, listFence);
    return;
  } else if (row.blob) {
    // A crash after the receipt write but before Blob release is a safe
    // duplicate. Compact it on the next pass under the same proof gate.
    const compacted = await db.updateAudioQueueRowIfPresent(
      row.id,
      expectedOwnerId,
      (current) =>
        current.registryState && UPLOAD_PROVED_REGISTRY_STATES.has(current.registryState)
          ? { ...current, blob: undefined }
          : null,
      // Releases the Blob: nothing to copy (KR8).
      null,
    );
    if (!compacted) return;
    row = compacted;
  }

  // Invariant: by this point row.storagePath is set. transcribeAudio
  // takes the path; the v2 endpoint downloads from Storage server-side.
  try {
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    const text = (await transcribeRegisteredQueueAudio(row.id, expectedOwnerId, listFence)).trim();
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    if (!text) {
      // Empty transcription = decoded silence/noise. Treat as a soft
      // failure (count it against the budget but don't tag the row as
      // permanently failed yet — a retry might help if the upstream
      // engine glitched).
      await bumpAttempt(row, expectedOwnerId, listFence);
      return;
    }
    // Success path — store the transcribed text so the resume card can
    // pre-load it on "Continue recording" without re-transcribing.
    const updated = await db.updateAudioQueueRowIfPresent(
      row.id,
      expectedOwnerId,
      (current) => ({ ...current, transcribedText: text }),
    );
    if (!updated) return;
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    queueList().updateItem(listFence, row.id, { transcribedText: text });
    await routeTranscribedText(updated, expectedOwnerId, listFence);
  } catch (err) {
    console.error('[Adia] queue processOne transcribe failed', (err as Error).name);
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    // V.B.1: terminal errors (413 payload-too-large, other 4xx malformed)
    // won't be helped by queue retries either — short-circuit to
    // terminal-fail so V.B's recovery banner surfaces immediately
    // instead of after ~2 minutes of futile retries. See grok.ts
    // classifyTranscribeStatus for the full classification table.
    if (err instanceof ParseError && err.terminal === true) {
      await markRowTerminallyFailed(row, expectedOwnerId, listFence);
      return;
    }
    // 429 (rate limit) and 401 (auth) — inline transcribeAudio already
    // skipped retrying them; the queue's 60s cycle may catch the reset.
    // Same fall-through as any other transient error.
    await bumpAttempt(row, expectedOwnerId, listFence);
  }
}

/**
 * V.B.1 terminal-fail short-circuit. Same end-state as `bumpAttempt`'s
 * exhausted branch but reached in one attempt instead of six. Audio
 * blob is RETAINED in IDB for the user's recovery flow (Type / Retry /
 * Discard via PrayerDetail's TranscriptionRecoveryBanner, shipping in
 * V.B.2).
 *
 * The linked Prayer flag and queue terminal state commit atomically. If the
 * prayer was already resolved or removed, only the still-present orphan row
 * may be marked failed; no stale projection is recreated.
 */
async function markRowTerminallyFailed(
  row: db.AudioQueueRow,
  expectedOwnerId: string,
  listFence: QueueFence,
): Promise<void> {
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  if (row.prayerId && normalizeRecoveryKind(row.recoveryKind) === 'new-prayer') {
    const failed = await db.failPendingPrayerTranscription(
      row.prayerId,
      row.id,
      expectedOwnerId,
      MAX_QUEUE_ATTEMPTS,
    );
    if (failed) {
      if (!(await ownerGateStillOpen(expectedOwnerId))) return;
      useAdiaStore.setState((state) => ({
        prayers: state.prayers.map((prayer) =>
          prayer.id === failed.prayer.id ? failed.prayer : prayer),
      }));
      queueList().updateItem(listFence, row.id, {
        attemptCount: failed.row.attemptCount,
        failed: true,
      });
      void markRegistryParked(
        row.id,
        failed.row.registryRevision,
        failed.row.recoveryLeaseToken,
        listFence,
      ).catch(() => {});
      return;
    }
  }
  const updated = await db.updateAudioQueueRowIfPresent(
    row.id,
    expectedOwnerId,
    (current) => ({
      ...current,
      attemptCount: MAX_QUEUE_ATTEMPTS,
      failed: true,
    }),
  );
  if (!updated) return;
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  queueList().updateItem(listFence, row.id, {
    attemptCount: MAX_QUEUE_ATTEMPTS,
    failed: true,
  });
  void markRegistryParked(
    row.id,
    updated.registryRevision,
    updated.recoveryLeaseToken,
    listFence,
  ).catch(() => {});
  // No setTimeout — terminal means we don't retry.
}

/**
 * Landing-refusal classification. Transient causes (read-only cutover, a
 * content-sync or lifecycle-context transition) are left for the next pass.
 * Everything else is a deterministic refusal of THIS pair (conflict row,
 * parked mutation, retry exhaustion, capture-row guard, unknown): latch it
 * terminal now so the Prayer carries its recovery banner instead of an empty
 * "saved" prayer that is silently retried forever.
 */
function isTransientLandingError(error: unknown): boolean {
  if (error instanceof CoreReadOnlyError) return true;
  const message = error instanceof Error ? error.message : '';
  return message.startsWith('content-sync-') || message.startsWith('content-lifecycle-context');
}

/**
 * Latch a linked new-prayer pair whose words could not be placed. This is the
 * rule #24 terminal transaction (db.failPendingPrayerTranscription: owner
 * binding, row.prayerId === prayerId, exact pointer, transcript/Blob/Storage
 * kept, capture guard, fence bump) and nothing else: NO markRegistryParked
 * (the registry row of an enhanced text-ready capture is 'transcribed', the
 * exact state 044 bind and reconcile require; parking it would make the
 * later reconcile impossible) and NO attempt bump (the attempt budget is for
 * transcription). Never throws: a refusal here is logged and the pass
 * continues with the next row.
 */
async function settlePendingPrayerLandingFailure(
  row: db.AudioQueueRow,
  expectedOwnerId: string,
  listFence: QueueFence,
  error: unknown,
): Promise<void> {
  console.error(
    '[Adia] pending prayer landing failed',
    error instanceof Error ? error.name : 'unknown',
    error instanceof Error ? error.message : '',
  );
  if (isTransientLandingError(error)) return;
  try {
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    if (!row.prayerId || normalizeRecoveryKind(row.recoveryKind) !== 'new-prayer') return;
    const failed = await db.failPendingPrayerTranscription(
      row.prayerId,
      row.id,
      expectedOwnerId,
      MAX_QUEUE_ATTEMPTS,
    );
    // null: the pair is no longer linked (landed elsewhere, removed, rebound);
    // nothing to latch.
    if (!failed) return;
    if (!(await ownerGateStillOpen(expectedOwnerId))) return;
    useAdiaStore.setState((state) => ({
      prayers: state.prayers.map((prayer) =>
        prayer.id === failed.prayer.id ? failed.prayer : prayer),
    }));
    queueList().updateItem(listFence, row.id, {
      attemptCount: failed.row.attemptCount,
      failed: true,
    });
  } catch (latchError) {
    console.error(
      '[Adia] pending prayer landing failure could not be recorded',
      latchError instanceof Error ? latchError.name : 'unknown',
    );
  }
}

/**
 * V.B.2: re-arm a terminally-failed queue row + linked Prayer for
 * another transcription cycle. Called by PrayerDetail's
 * TranscriptionRecoveryBanner when the user taps "Retry transcription".
 *
 * The prayer flag and queue retry state commit in one owner-checked IDB
 * transaction. This also prevents a stale tab from recreating a discarded
 * row after another tab resolved the recovery flow.
 *
 * Defensive guards (R1 in V.B Phase 2 plan):
 *   - prayer.pendingTranscriptionId must be set
 *   - the queue row must exist
 *   - the queue row's prayerId must match prayer.id (the row is
 *     genuinely linked to this prayer, not an awaitingInlineSave
 *     orphan or a different prayer's row)
 *   - the queue row must currently be failed: true
 * Throws on any guard violation; the banner renders the localized
 * error message and disables Retry until the user picks a different
 * action.
 */
export async function retryFailedTranscription(prayer: Prayer): Promise<void> {
  // THROWS rather than returning silently. Its only caller is the
  // recovery banner, which treats a resolved promise as "the state
  // transition happened" and a rejection as "tell the user it did not".
  // A silent return would leave the banner reporting a retry that never
  // started. Same reason discardFailedAudio throws below.
  assertCoreWriteAllowed('audio:retry-failed');
  if (!enhancedVoiceAllowed()) {
    throw new Error('retryFailedTranscription: enhanced voice disabled');
  }
  const listFence = captureQueueListFence();
  const expectedOwnerId = await getCurrentUserId();
  if (!expectedOwnerId || !(await ownerGateStillOpen(expectedOwnerId))) {
    throw new Error('retryFailedTranscription: owner changed');
  }
  if (!prayer.pendingTranscriptionId) {
    throw new Error('retryFailedTranscription: prayer has no pendingTranscriptionId');
  }
  const queueItemId = prayer.pendingTranscriptionId;
  const publishPrayer = (next: Prayer): void => {
    useAdiaStore.setState((state) => ({
      prayers: state.prayers.map((item) => (item.id === next.id ? next : item)),
    }));
  };
  // The processor's own per-owner, per-row Web Lock, held across the local
  // re-arm and the registry transition: a queue pass that starts in between
  // skips this row (ifAvailable) instead of claiming a transcription from a
  // registry row that is still failed/parked.
  await runInlineAudioExclusive(queueItemId, expectedOwnerId, async () => {
    if (!(await ownerGateStillOpen(expectedOwnerId))) {
      throw new Error('retryFailedTranscription: owner changed');
    }
    // Re-arm the local pair FIRST. Rule #24's atomic transaction requires the
    // latched row (failed === true). The registry reply below merges
    // `failed: remote.failed`, which is false for 'retry', so a retry sent
    // before the re-arm made the re-arm refuse ("recovery state changed") and
    // the words never landed (the pre-repair order).
    const rearmed = await db.rearmPendingPrayerTranscription(
      prayer.id,
      queueItemId,
      expectedOwnerId,
    );
    if (!rearmed) {
      throw new Error('retryFailedTranscription: recovery state changed');
    }
    // A row that already holds its transcript failed to LAND, not to transcribe.
    // Its registry row is 'transcribed', from which 044 retry_audio_capture_v1 is
    // refused (it accepts failed/parked/retry), and a parked-then-retried row
    // could never reconcile. The retained words are re-routed by the pass's
    // text-ready branch with no network; bind and reconcile then run against the
    // unchanged 'transcribed' registry row. Rows without a transcript (real
    // transcription failures) keep the server retry transition.
    if (rearmed.row.captureMode !== 'enhanced' || rearmed.row.transcribedText?.trim()) {
      queueList().updateItem(listFence, rearmed.row.id, {
        attemptCount: rearmed.row.attemptCount,
        failed: false,
      });
      publishPrayer(rearmed.prayer);
      return;
    }
    try {
      // The reply's merge publishes the row (registryState 'retry', failed
      // false, attemptCount max(local 0, server count)) to the list itself.
      await retryRegistryAudio(
        rearmed.row.id,
        rearmed.row.registryRevision,
        rearmed.row.recoveryLeaseToken,
        listFence,
      );
    } catch (error) {
      // The server refused the transition (or the request failed): latch the
      // pair terminal again through the same rule #24 transaction, so the row
      // is not left re-armed against a registry row that stays failed/parked
      // and the banner keeps its Retry/Type/Remove actions. The Prayer was not
      // published to the store in between, so the mounted banner shows the
      // caller's localized error.
      try {
        const relatched = await db.failPendingPrayerTranscription(
          prayer.id,
          queueItemId,
          expectedOwnerId,
          MAX_QUEUE_ATTEMPTS,
        );
        if (relatched) {
          publishPrayer(relatched.prayer);
          queueList().updateItem(listFence, queueItemId, {
            attemptCount: relatched.row.attemptCount,
            failed: true,
          });
        }
      } catch (latchError) {
        console.error(
          '[Adia] retryFailedTranscription: re-latch after a refused registry retry failed',
          latchError instanceof Error ? latchError.name : 'unknown',
        );
      }
      throw error;
    }
    publishPrayer(rearmed.prayer);
  });
  // Trigger an immediate processor pass — don't wait for the next
  // online/visibility tick. Requested after the lock above is released so the
  // pass can take the row's lock (ifAvailable) and transcribe it now.
  processQueueInBackground();
}

/** Re-arm failed audio that belongs to an update or testimony editor. */
export async function retryDestinationAudio(queueItemId: string): Promise<void> {
  assertCoreWriteAllowed('audio:retry-destination');
  if (!enhancedVoiceAllowed()) {
    throw new Error('retryDestinationAudio: enhanced voice disabled');
  }
  const listFence = captureQueueListFence();
  const expectedOwnerId = await getCurrentUserId();
  if (!expectedOwnerId || !(await ownerGateStillOpen(expectedOwnerId))) {
    throw new Error('retryDestinationAudio: owner changed');
  }
  const currentAudio = await db.getAudioQueueRow(queueItemId);
  if (currentAudio?.captureMode === 'enhanced') {
    await retryRegistryAudio(
      currentAudio.id,
      currentAudio.registryRevision,
      currentAudio.recoveryLeaseToken,
      listFence,
    );
  }
  const updated = await db.updateAudioQueueRowIfPresent(
    queueItemId,
    expectedOwnerId,
    (row) => {
      if (
        !row.prayerId ||
        normalizeRecoveryKind(row.recoveryKind) === 'new-prayer' ||
        row.failed !== true
      ) return null;
      return { ...row, attemptCount: 0, failed: false };
    },
  );
  if (!updated) {
    throw new Error('retryDestinationAudio: invalid recovery row');
  }
  queueList().updateItem(listFence, queueItemId, {
    attemptCount: 0,
    failed: false,
  });
  processQueueInBackground();
}

/**
 * V.B.2.1: terminate a failed transcription. Called by PrayerDetail's
 * TranscriptionRecoveryBanner when the user confirms "Remove this
 * prayer". The Prayer record is deleted entirely (V.B.2 only cleared
 * flags, which left a half-state Prayer with empty rawText visible in
 * the vine — behavior didn't match the "Remove this prayer" copy).
 *
 * Crash-safety ordering: exact registry discard/deletion receipt first,
 * local-only pending Prayer removal second. If provider cleanup cannot be
 * proven, the visible recovery surface remains available for retry and the
 * only copy never becomes an invisible orphan.
 *
 * Rule #32 dependency: this helper relies on the sync gate at
 * `pushPrayer` rejecting Prayers with `pendingTranscriptionId` set
 * (sync.ts line ~256). Failed-transcription Prayers are therefore
 * never pushed to Supabase, so `removePrayer`'s fire-and-forget
 * `deletePrayerRemote` no-ops cleanly (UPDATE matches zero rows).
 * If that gate is ever loosened, this helper would start mutating
 * cloud state — the dependency is structural and load-bearing.
 *
 * Defensive guards:
 *   - prayer.pendingTranscriptionId must be set
 *   - if a queue row exists, its prayerId must match prayer.id
 *     (safety check before destroying audio that might belong to
 *     another prayer)
 * A missing queue row is OK (already cleaned up by some other path);
 * we just delete the Prayer.
 */
export async function discardFailedAudio(prayer: Prayer): Promise<void> {
  // Read-only: this path DELETES a prayer and its audio. Never in this
  // release. THROWS rather than returning silently: the recovery banner
  // calls onDeleted() on the resolved path, which closes the detail
  // overlay as if the prayer were gone, and the prayer is still in the
  // store and reappears in the Garden. A rejection routes to the
  // banner's catch, which says the delete failed, which is true.
  assertCoreWriteAllowed('audio:discard-failed');
  if (!enhancedVoiceAllowed()) {
    throw new Error('discardFailedAudio: enhanced voice disabled');
  }
  const listFence = captureQueueListFence();
  const expectedOwnerId = await getCurrentUserId();
  if (!expectedOwnerId || !(await ownerGateStillOpen(expectedOwnerId))) {
    throw new Error('discardFailedAudio: owner changed');
  }
  if (!prayer.pendingTranscriptionId) {
    throw new Error('discardFailedAudio: prayer has no pendingTranscriptionId');
  }
  const row = await db.getAudioQueueRow(prayer.pendingTranscriptionId);
  if (row && row.prayerId !== prayer.id) {
    throw new Error('discardFailedAudio: queue row prayerId mismatch');
  }
  if (!row) {
    // LC9: no recording row, so only the never-synced Prayer would be removed.
    // The object this was called with can be stale (another tab may have typed
    // the words, clearing the pointer and authoring the lifecycle Prayer), so
    // under the recording's owner lock, the same one the typed rescue holds,
    // the durable Prayer is read again and removed only while it still names
    // this exact recording, is never-synced (no lifecycle entity) and no row
    // with that id has come back. Otherwise nothing is removed.
    const queueItemId = prayer.pendingTranscriptionId;
    await runInlineAudioExclusive(queueItemId, expectedOwnerId, async () => {
      const [durable, entity, current] = await Promise.all([
        db.getPrayer(prayer.id),
        getLocalContentEntity(expectedOwnerId, 'prayer', prayer.id),
        db.getAudioQueueRow(queueItemId),
      ]);
      const stillPending = Boolean(durable) && durable?.pendingTranscriptionId === queueItemId && !entity && !current;
      if (!stillPending) throw new Error('discardFailedAudio: prayer changed');
      if (!(await ownerGateStillOpen(expectedOwnerId))) {
        throw new Error('discardFailedAudio: owner changed');
      }
      await useAdiaStore.getState().removePrayer(prayer.id);
    });
    return;
  }
  // Step 1: transition and prove deletion of the exact capture (skip if a
  // prior idempotent attempt already removed the row).
  if (row) {
    const queueItemId = prayer.pendingTranscriptionId;
    const discarded = await dequeueAudio(
      queueItemId,
      expectedOwnerId,
      listFence,
      (current) => current.prayerId === prayer.id,
    );
    if (!discarded) {
      throw new Error('discardFailedAudio: deletion receipt unavailable');
    }
  }
  // Step 2: remove the never-synced local Prayer only after audio disposition
  // is durable. The banner remains mounted if step 1 fails.
  await useAdiaStore.getState().removePrayer(prayer.id);
}

/**
 * Route a queue row's transcribedText to its destination:
 *   - prayerId set: write text to Prayer.rawText, clear pendingTranscriptionId,
 *     dequeue the audio (R7).
 *   - prayerId null: leave the entry in queue with text populated; the
 *     resume card consumes it on "Continue recording" / "Save what I have".
 */
async function routeTranscribedText(
  row: db.AudioQueueRow,
  expectedOwnerId: string,
  listFence: QueueFence = queuePassFence,
): Promise<void> {
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  if (!row.transcribedText) return;
  if (normalizeRecoveryKind(row.recoveryKind) !== 'new-prayer') {
    // The exact editor claims this durable text and dequeues it only after
    // the user's update or testimony save commits.
    return;
  }
  if (!row.prayerId) {
    // Still waiting for the user's resume-card decision.
    return;
  }
  let context: ContentLifecycleContext;
  try {
    context = await getCurrentContentLifecycleContext();
  } catch {
    // Auth or the owner binding is mid-transition; the next pass retries.
    return;
  }
  if (context.ownerId !== expectedOwnerId) return;
  let completed: Awaited<ReturnType<typeof authorRecoveredPrayerTranscription>>;
  try {
    completed = await authorRecoveredPrayerTranscription(context, row.id);
  } catch (error) {
    await settlePendingPrayerLandingFailure(row, expectedOwnerId, listFence, error);
    return;
  }
  if (!completed || !(await ownerGateStillOpen(expectedOwnerId))) return;
  const store = useAdiaStore.getState();
  if (store.prayers.some((prayer) => prayer.id === completed.prayer.id)) {
    useAdiaStore.setState((state) => ({
      prayers: state.prayers.map((prayer) =>
        prayer.id === completed.prayer.id ? completed.prayer : prayer),
    }));
  } else {
    await store.loadPrayers();
  }
  queueList().updateItem(listFence, row.id, {
    localDisposition: 'destination-commit-pending',
    destinationEntityType: 'prayer',
    destinationEntityId: completed.prayer.id,
    destinationOpId: completed.destinationOpId,
  });
  try {
    await syncContentLifecycle();
    await finalizeCommittedAudio(row.id, expectedOwnerId, listFence);
  } catch {
    // Durable local binding remains; reconnect retries without re-appending.
  }
}

async function bumpAttempt(
  row: db.AudioQueueRow,
  expectedOwnerId: string,
  listFence: QueueFence,
  // KR8: the attempt's own proved copy (or `null` when it had none); omitted,
  // the writes below prove one themselves.
  verifiedCopy?: db.VerifiedCaptureCopy | null,
): Promise<void> {
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  const nextAttempt = row.attemptCount + 1;
  const exhausted = nextAttempt >= MAX_QUEUE_ATTEMPTS;
  if (
    exhausted &&
    row.prayerId &&
    normalizeRecoveryKind(row.recoveryKind) === 'new-prayer'
  ) {
    const failed = await db.failPendingPrayerTranscription(
      row.prayerId,
      row.id,
      expectedOwnerId,
      MAX_QUEUE_ATTEMPTS,
      verifiedCopy,
    );
    if (failed) {
      if (!(await ownerGateStillOpen(expectedOwnerId))) return;
      useAdiaStore.setState((state) => ({
        prayers: state.prayers.map((prayer) =>
          prayer.id === failed.prayer.id ? failed.prayer : prayer),
      }));
      queueList().updateItem(listFence, row.id, {
        attemptCount: failed.row.attemptCount,
        failed: true,
      });
      void markRegistryParked(
        row.id,
        failed.row.registryRevision,
        failed.row.recoveryLeaseToken,
        listFence,
      ).catch(() => {});
      return;
    }
  }
  const updated = await db.updateAudioQueueRowIfPresent(
    row.id,
    expectedOwnerId,
    (current) => ({
      ...current,
      attemptCount: nextAttempt,
      failed: exhausted,
    }),
    verifiedCopy,
  );
  if (!updated) return;
  if (!(await ownerGateStillOpen(expectedOwnerId))) return;
  queueList().updateItem(listFence, row.id, {
    attemptCount: nextAttempt,
    failed: exhausted,
  });
  if (exhausted) {
    void markRegistryParked(
      row.id,
      updated.registryRevision,
      updated.recoveryLeaseToken,
      listFence,
    ).catch(() => {});
  }
  if (!exhausted) {
    // Schedule the next attempt cycle. The simple setTimeout is fine
    // for the ~2 minute envelope; if the tab is hidden during the wait
    // browsers may throttle, but `online` and `visibilitychange` events
    // both call processQueue too, so a hidden tab won't stall forever.
    setTimeout(() => {
      processQueueInBackground();
    }, RETRY_CYCLE_MS);
  }
}

// ---- Part R: kept recordings (account-change recording plan rev 4.1, section 9;
// Part R proposal revision 2 and amendment 1) ------------------------------------

export type KeptCaptureDestination = 'home' | 'update' | 'testimony' | 'bookmark';

/** One eligible kept recording, as listed for one scope. */
export interface KeptCaptureItem {
  id: string;
  ownerId: string;
  capturedAt: string;
  destination: KeptCaptureDestination;
  /** The destination exists now (re-checked when Use is pressed). */
  available: boolean;
  /** Audio (Enhanced Voice) rather than browser-speech text. */
  carriesAudio: boolean;
  /** `strictRowSnapshot` of the row as listed (adoption requires it unchanged). */
  snapshot: string;
  /** Text evidence only: the exact text whose SHA-256 matched the evidence. */
  verifiedText: string | null;
  /** The row as listed, held in memory for the same-capture check. */
  listedRow: db.AudioQueueRow;
  listedAtMs: number;
}

export interface KeptCaptureListing {
  fence: NonNullable<QueueFence>;
  items: KeptCaptureItem[];
}

/** The list scope is still exactly `fence`: same runtime, owner and epoch, and
 * the current auth user and epoch equal it. */
export function queueScopeStillCurrent(fence: QueueFence): fence is NonNullable<QueueFence> {
  const scope = captureQueueListFence();
  return Boolean(
    fence &&
    scope &&
    scope.runtimeId === fence.runtimeId &&
    scope.ownerId === fence.ownerId &&
    scope.authEpoch === fence.authEpoch &&
    getAuthUserId() === fence.ownerId &&
    getAuthEpoch() === fence.authEpoch,
  );
}

const SHA256_HEX = /^[0-9a-f]{64}$/;

async function sha256BytesHex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256TextHex(text: string): Promise<string> {
  const encoded = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest(
    'SHA-256',
    encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength),
  );
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

function keptCaptureDestination(row: db.AudioQueueRow): KeptCaptureDestination {
  switch (row.recoveryKind) {
    case 'progress-update':
      return 'update';
    case 'testimony':
      return 'testimony';
    case 'bookmark':
      return 'bookmark';
    default:
      return 'home';
  }
}

// The two build-time Bible flags, with the exact semantics of
// `BIBLE_ENABLED` / `BIBLE_BOOKMARKS_ENABLED` in feature-flags.ts. Read here
// with optional chaining (the core-cutover.ts pattern, rule #85) because
// feature-flags.ts reads `import.meta.env` without it, and plain-Node probes
// that import this module run without a Vite env.
const BIBLE_DESTINATION_ENABLED = import.meta.env?.VITE_BIBLE_ENABLED === 'true';
const BIBLE_BOOKMARK_DESTINATION_ENABLED = import.meta.env?.VITE_BIBLE_BOOKMARKS_ENABLED === 'true';

/** The bookmark sheet exists and can compose for this exact passage. */
export function bookmarkRecoveryDestinationAvailable(anchor: unknown): boolean {
  return BIBLE_DESTINATION_ENABLED &&
    BIBLE_BOOKMARK_DESTINATION_ENABLED &&
    !isCoreReadOnly() &&
    parseScriptureAnchor(anchor) !== null;
}

async function keptCaptureDestinationAvailable(row: db.AudioQueueRow): Promise<boolean> {
  const destination = keptCaptureDestination(row);
  if (destination === 'home') return true;
  if (destination === 'bookmark') return bookmarkRecoveryDestinationAvailable(row.scriptureAnchor);
  if (!row.prayerId) return false;
  return (await db.getPrayer(row.prayerId)) !== undefined;
}

/**
 * Eligible kept recordings of the current scope's owner, newest first (plan
 * 9.1). Read from IndexedDB; a row that fails any check, including text whose
 * hash does not match its evidence, is never listed and never written. Returns
 * null when the scope changed during the read (nothing is shown for it).
 */
export async function listKeptCaptures(): Promise<KeptCaptureListing | null> {
  const fence = captureQueueListFence();
  if (!queueScopeStillCurrent(fence)) return null;
  const listedAtMs = Date.now();
  const rows = await db.getAllAudioQueueRows();
  const items: KeptCaptureItem[] = [];
  for (const row of rows) {
    // One malformed row is skipped, never repaired, and never hides the others.
    let item: KeptCaptureItem | null = null;
    try {
      if (db.keptCaptureRefusal(row, fence.ownerId) !== null) continue;
      const snapshot = db.strictRowSnapshot(row);
      if (snapshot === null) continue;
      const evidence = row.captureEvidence as db.CaptureEvidenceV1;
      let verifiedText: string | null = null;
      if (evidence.kind === 'text') {
        const text = row.transcribedText as string;
        if ((await sha256TextHex(text)) !== evidence.sha256) continue;
        verifiedText = text;
      }
      item = {
        id: row.id,
        ownerId: fence.ownerId,
        capturedAt: row.capturedAt,
        destination: keptCaptureDestination(row),
        available: await keptCaptureDestinationAvailable(row),
        carriesAudio: evidence.kind === 'blob',
        snapshot,
        verifiedText,
        listedRow: row,
        listedAtMs,
      };
    } catch {
      item = null;
    }
    if (item) items.push(item);
    if (!queueScopeStillCurrent(fence)) return null;
  }
  if (!queueScopeStillCurrent(fence)) return null;
  items.sort((a, b) => b.capturedAt.localeCompare(a.capturedAt));
  return { fence, items };
}

export type KeptCaptureAdoptionOutcome =
  | { status: 'adopted' }
  | { status: 'already-restored' }
  | { status: 'unavailable' }
  | { status: 'refused'; reason: string }
  | { status: 'stale' };

/**
 * Use: adopt one listed kept recording for the scope it was listed under
 * (plan 9.2, proposal revision 2 section 4, amendment A3.2). The destination is
 * re-checked; the adoption transaction re-checks everything else. After it,
 * the list is refreshed and the existing lanes take over (a bookmark
 * recording with audio also starts its word preparation once). A refusal may
 * be the same capture already adopted by another tab: recognised only by
 * `keptCaptureAdoptedBySameCapture` with the binding and auth re-read, never by
 * `adoptedAt` alone. Nothing here retargets, saves content or deletes.
 */
export async function adoptKeptCaptureForRuntime(
  item: KeptCaptureItem,
  fence: QueueFence,
): Promise<KeptCaptureAdoptionOutcome> {
  if (!queueScopeStillCurrent(fence) || fence.ownerId !== item.ownerId) return { status: 'stale' };
  const ownerId = fence.ownerId;
  const authEpoch = fence.authEpoch;
  if (!(await keptCaptureDestinationAvailable(item.listedRow))) {
    return queueScopeStillCurrent(fence) ? { status: 'unavailable' } : { status: 'stale' };
  }
  if (!queueScopeStillCurrent(fence)) return { status: 'stale' };
  const destination = keptCaptureDestination(item.listedRow);
  const result = await db.adoptKeptCapture({
    rowId: item.id,
    ownerId,
    authEpoch,
    snapshot: item.snapshot,
    verifiedText: item.verifiedText,
    destinationPrayerId: destination === 'update' || destination === 'testimony'
      ? item.listedRow.prayerId
      : null,
    adoptedAt: new Date().toISOString(),
  }, () => ({ userId: getAuthUserId(), epoch: getAuthEpoch() }));
  if (result.status === 'adopted') {
    // The durable adoption stands; a changed scope publishes nothing.
    if (!queueScopeStillCurrent(fence)) return { status: 'stale' };
    await queueList().refreshItem(item.id);
    if (!queueScopeStillCurrent(fence)) return { status: 'stale' };
    processQueueInBackground();
    if (destination === 'bookmark' && item.carriesAudio && enhancedVoiceAllowed()) {
      void prepareBookmarkRecordingWords(item.id, { explicit: false });
    }
    return { status: 'adopted' };
  }
  if (result.reason === 'destination-unavailable') {
    return queueScopeStillCurrent(fence) ? { status: 'unavailable' } : { status: 'stale' };
  }
  const current = await db.getAudioQueueRow(item.id);
  const binding = await getContentOwnerBinding();
  if (!queueScopeStillCurrent(fence)) return { status: 'stale' };
  if (
    binding === ownerId &&
    getAuthUserId() === ownerId &&
    db.keptCaptureAdoptedBySameCapture(current, item.listedRow, ownerId, item.listedAtMs)
  ) {
    await queueList().refreshItem(item.id);
    return queueScopeStillCurrent(fence) ? { status: 'already-restored' } : { status: 'stale' };
  }
  return { status: 'refused', reason: result.reason };
}

// ---- Part R: explicit word preparation for bookmark audio (amendment A2) ----------

/** How the latest explicit run for a bookmark recording ended on this page. */
export type BookmarkPreparationOutcome =
  | 'ready'
  | 'failed'
  | 'waiting'
  | 'check-later'
  | 'voice-off'
  | 'stored'
  | 'stale';

const bookmarkRuns = new Map<string, Promise<BookmarkPreparationOutcome>>();
/** Keyed `${ownerId}:${authEpoch}:${queueId}`: never read by another owner or epoch. */
const bookmarkOutcomes = new Map<string, BookmarkPreparationOutcome>();
const BOOKMARK_OUTCOME_LIMIT = 64;

function bookmarkOutcomeKey(ownerId: string, authEpoch: number, queueId: string): string {
  return `${ownerId}:${authEpoch}:${queueId}`;
}

export function lastBookmarkPreparationOutcome(
  queueId: string,
  ownerId: string,
  authEpoch: number,
): BookmarkPreparationOutcome | null {
  return bookmarkOutcomes.get(bookmarkOutcomeKey(ownerId, authEpoch, queueId)) ?? null;
}

function rememberBookmarkOutcome(key: string, outcome: BookmarkPreparationOutcome): void {
  bookmarkOutcomes.delete(key);
  if (outcome !== 'stale') bookmarkOutcomes.set(key, outcome);
  while (bookmarkOutcomes.size > BOOKMARK_OUTCOME_LIMIT) {
    const oldest = bookmarkOutcomes.keys().next().value;
    if (oldest === undefined) break;
    bookmarkOutcomes.delete(oldest);
  }
}

const TRANSCRIBABLE_REGISTRY_STATES = new Set(['uploaded', 'retry', 'transcribing', 'transcribed']);

/** The bookmark row this path may work on (amendment A2.2). */
function bookmarkRowPreparable(row: db.AudioQueueRow | undefined, ownerId: string): boolean {
  if (!row || row.ownerId !== ownerId) return false;
  if (
    row.recoveryKind !== 'bookmark' ||
    row.inlineSaveSource !== 'bookmark' ||
    row.awaitingInlineSave !== true ||
    row.localDisposition !== 'unresolved' ||
    (row.destinationState !== undefined && row.destinationState !== 'unresolved') ||
    row.captureMode !== 'enhanced' ||
    row.captureEvidence?.kind !== 'blob' ||
    parseScriptureAnchor(row.scriptureAnchor) === null
  ) return false;
  // `failed` mirrors the server (`failed` / `parked`); never set locally here.
  return row.failed !== true || row.registryState === 'failed' || row.registryState === 'parked';
}

function retiredError(error: unknown): boolean {
  return error instanceof Error && error.message === CAPTURE_RETIRED_ERROR;
}

/**
 * Get the words for one bookmark recording that holds audio but no words,
 * through the existing registry authority (amendment A2.3). One run per call
 * (single-flight per page; the cross-tab Web Lock serialises tabs): at most one
 * upload, one retry action (only when `explicit`), one recovery claim to
 * refresh a stale state, and two transcription helper calls. No timers, no
 * polling, no automatic retry of a terminal failure. It never sets or clears
 * `failed`, `attemptCount` or `awaitingInlineSave` and never changes the
 * anchor, lane or destination; only authoritative registry merges change
 * mirrored fields. Every step, request, write and list update stops when the
 * owner, auth epoch or list scope captured at the call changes.
 */
export function prepareBookmarkRecordingWords(
  queueId: string,
  options: { explicit: boolean },
): Promise<BookmarkPreparationOutcome> {
  const existing = bookmarkRuns.get(queueId);
  if (existing) return existing;
  const fence = captureQueueListFence();
  if (!queueScopeStillCurrent(fence)) return Promise.resolve('stale');
  const ownerId = fence.ownerId;
  const authEpoch = fence.authEpoch;
  const outcomeKey = bookmarkOutcomeKey(ownerId, authEpoch, queueId);
  const run = (async (): Promise<BookmarkPreparationOutcome> => {
    if (!enhancedVoiceAllowed()) return 'voice-off';
    if (isCoreReadOnly()) return 'stored';
    explicitContinuations.set(queueId, () => queueScopeStillCurrent(fence));
    try {
      return await runInlineAudioExclusive(queueId, ownerId, () =>
        prepareBookmarkWordsLocked(queueId, ownerId, fence, options.explicit));
    } catch (error) {
      if (retiredError(error)) return 'stale';
      console.error('[Adia] bookmark-words-run-failed', error instanceof Error ? error.message : 'unknown');
      return 'waiting';
    } finally {
      explicitContinuations.delete(queueId);
    }
  })();
  bookmarkRuns.set(queueId, run);
  notifyAudioActivity();
  void run.then((outcome) => {
    bookmarkRuns.delete(queueId);
    if (queueScopeStillCurrent(fence)) rememberBookmarkOutcome(outcomeKey, outcome);
    notifyAudioActivity();
  });
  return run;
}

async function prepareBookmarkWordsLocked(
  queueId: string,
  ownerId: string,
  fence: NonNullable<QueueFence>,
  explicit: boolean,
): Promise<BookmarkPreparationOutcome> {
  const mayContinue = () => captureMayContinue(queueId);
  let refreshed = false;
  let retried = false;
  let uploaded = false;
  let transcribeCalls = 0;
  let row = await db.getAudioQueueRow(queueId);
  for (let step = 0; step < 8; step += 1) {
    assertCaptureMayContinue(queueId);
    if (!row || !bookmarkRowPreparable(row, ownerId)) return 'stored';
    if (row.transcribedText?.trim()) return 'ready';
    const state = row.registryState;
    try {
      if (row.blob && (!state || state === 'registered')) {
        if (uploaded) return 'waiting';
        uploaded = true;
        await uploadAudioToStorage(row.blob, queueId, ownerId, extFromMimeType(row.mimeType), fence);
      } else if (row.blob && state && UPLOAD_PROVED_REGISTRY_STATES.has(state) && row.storagePath) {
        await persistUploadedAudioPointer(queueId, row.storagePath, ownerId);
      } else if (!state || state === 'registered') {
        // No Blob and no proved upload: nothing this path can recover.
        return 'stored';
      } else if (state === 'failed' || state === 'parked') {
        if (!explicit || retried) return 'failed';
        retried = true;
        await retryRegistryAudio(
          queueId,
          row.registryRevision,
          row.recoveryLeaseToken,
          fence,
          mayContinue,
        );
      } else if (TRANSCRIBABLE_REGISTRY_STATES.has(state)) {
        if (transcribeCalls >= 2) return 'waiting';
        transcribeCalls += 1;
        const text = await transcribeRegisteredQueueAudio(queueId, ownerId, fence);
        if (!text) return 'failed';
        await persistInlineTranscription(queueId, text, ownerId);
        return 'ready';
      } else {
        return 'stored';
      }
    } catch (error) {
      if (retiredError(error)) throw error;
      const code = error instanceof ParseError
        ? error.serverCode
        : error instanceof AudioRegistryProtocolError
          ? error.code
          : undefined;
      if (code === 'lease_conflict') return 'check-later';
      if (code === 'not_found') return 'stored';
      if (code === 'state_conflict') {
        if (refreshed || !row?.registryRevision) return 'check-later';
        refreshed = true;
        // A refresh through the existing recovery claim (it grants or rotates
        // this device's lease; a stale revision returns the current record).
        try {
          await claimRegistryRecovery(row, fence, mayContinue);
        } catch (claimError) {
          if (retiredError(claimError)) throw claimError;
          const claimCode = claimError instanceof AudioRegistryProtocolError ? claimError.code : undefined;
          // Quarantined on the server, or its destination already resolved:
          // nothing this path may do. Kept as it is.
          if (claimCode === 'state_conflict' || claimCode === 'not_found') return 'stored';
          return 'check-later';
        }
      } else if (error instanceof ParseError) {
        if (error.registryState === 'failed' || error.registryState === 'parked') return 'failed';
        if (error.terminal) return 'failed';
        return 'waiting';
      } else if (error instanceof db.CaptureBytesUnverifiedError) {
        // KR8: the stored bytes could not be read or did not match their
        // capture evidence. Nothing was written or sent and the recording is
        // kept; this is not a connection problem, so it is never 'waiting'
        // ("needs an internet connection").
        console.error('[Adia] bookmark-words-bytes-unverified', error.reason);
        return 'failed';
      } else {
        // Code text only (never content), for diagnosing a stopped run.
        console.error('[Adia] bookmark-words-step-failed', error instanceof Error ? error.message : 'unknown');
        return 'waiting';
      }
    }
    assertCaptureMayContinue(queueId);
    row = await db.getAudioQueueRow(queueId);
  }
  return 'waiting';
}

/** What a kept or recovered recording's words are doing now (amendment A3.1). */
export type RecordingWordsStatus =
  | 'ready'
  | 'processing'
  | 'check-later'
  | 'waiting'
  | 'failed'
  | 'voice-off';

export function recordingWordsStatus(
  item: Pick<QueueItemMeta, 'id' | 'transcribedText' | 'mimeType' | 'sizeBytes' | 'storagePath' | 'registryState'>,
  ownerId: string,
  authEpoch: number,
): RecordingWordsStatus {
  if (item.transcribedText?.trim()) return 'ready';
  if (isAudioQueueItemInFlight(item.id)) return 'processing';
  if (audioQueueMetaCarriesBytes(item) && !enhancedVoiceAllowed()) return 'voice-off';
  const last = lastBookmarkPreparationOutcome(item.id, ownerId, authEpoch);
  if (last === 'check-later') return 'check-later';
  if (last === 'failed' || last === 'stored') return 'failed';
  if (item.registryState === 'failed' || item.registryState === 'parked') return 'failed';
  return 'waiting';
}
